/**
 * Kunden-Detail (H4: Vollseite statt Drawer, Design-Vorlage 3e) — Breadcrumb, Kontaktkarte,
 * Lifetime-KPIs, gemischte Historie (Anfragen/Angebote/Events/Zahlungen, filterbar) und
 * Notizen mit Autor/Datum + Eingabe. Alle Zahlen/Interaktionen aus echten API-Daten.
 */
import { zahlungsstandMeta } from "../lib/zahlungsstand";
import { KOMMUNIKATIONSSPRACHEN, SPRACH_LABEL, type Kommunikationssprache } from "@maestro/kommunikationssprache";
import { Fragment, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { anreicherungsAnzeige, type Trefferliste } from "@maestro/anreicherung";
import { MODUL_PLACES, OHNE_WERT, registerFelder, registerTrefferauswahl } from "@maestro/firmenprofil";
import { zeigtGoogleAngaben } from "@maestro/google-attribution";
import { trefferauswahl } from "@maestro/places-anzeige";
import { adressAbgleich, gleifAlsAdressQuelle, gleifKreuzcheck, GEGENUEBERSTELLUNG_TITEL } from "@maestro/quellenabgleich";
import { standZeile } from "@maestro/recherche-stand";
import { ustIdKarte, type UstIdZeile } from "@maestro/ustid-anzeige";
import { useApi, apiMutate } from "../hooks/useApi";
import { apiFetch } from "../lib/api";
import { neuabrufAnstossen } from "../lib/neuabruf";
import { placesErgebnisVerwerfen, registerErgebnisVerwerfen, registerTrefferWaehlen } from "../lib/zuordnung";
import { currentMembership } from "../lib/membership";
import { ANFRAGEN_PFAD, angebotPfad, vorgangPfad, zahlungenPfad } from "../lib/pfade";
import { useMitHerkunft } from "../lib/rueckweg";
import { adresseEinzeilig } from "../lib/adresse-anzeige";
import { landChip } from "../lib/land-hinweis";
import { LAENDER } from "../lib/laender";
import { AdresseFelder, type AdresseWerte } from "../ui/AdresseFelder";
import type { Customer, CustomerNote, EventRow, GooglePlacesApplicableField, GooglePlacesLookup, HandelsregisterLookup, Me, Payment, UstIdAntwort } from "../ui/types";
import { anlassAnzeige, PAYMENT_STATUS, PAYMENT_TYPE_LABEL, PILL_CLASSES, eventStatusMeta, formatDate, money } from "../ui/format";
import { Button, Card, CardRow, Drawer, EmptyState, ErrorNote, Icon, Input, KpiCard, LoadingRows, Rueckweg, Select, StatusPill, Checkbox, ButtonLink, ButtonRouterLink } from "../ui/components";
import { useContentRefetch } from "../ui/CopilotProvider";
import { GoogleAttribution } from "../ui/GoogleAttribution";
import { KundenhistorieBlock } from "../ui/KundenhistorieBlock";
import { AutomatischErkanntChip, KundentypAuswahl, erkennungsGrund, kundentypVon, type Kundentyp } from "../ui/Kundentyp";

/**
 * F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md B0 "L7"): Status eines Vorgangs, der noch VOR
 * der Zusage steht. Handgespiegelt (kein gemeinsames Paket mit `apps/api`, gleiches Prinzip wie
 * die uebrigen Handspiegel-Typen dieses Repos) — `cancelled` zaehlt zusaetzlich nur dazu, wenn nie
 * zugesagt wurde (`wonAt == null`, s. `inquiryCount` unten).
 */
const ANFRAGE_PHASE_STATUS = new Set(["inquiry", "offer_draft", "offer_sent", "offer_declined", "no_response"]);

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean).slice(0, 2);
  const out = parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
  return out || "?";
}

type HistoryCategory = "anfrage" | "angebot" | "event" | "zahlung";
interface HistoryEntry {
  id: string;
  at: string;
  category: HistoryCategory;
  icon: string;
  title: string;
  detail: string;
  pill?: { label: string; cls: string };
  href?: string;
  /** Kostenuebernahme-Sichtbarkeit (Domenico/STORIA-Feedback, 07.09.2026): nur auf dem
   *  "Anfrage eingegangen"-Eintrag gesetzt (existiert fuer jedes Event genau einmal) - damit
   *  taucht die Kennzeichnung pro Event genau einmal in der Historie auf, nicht bei jedem
   *  Lebenszyklus-Eintrag desselben Events erneut. */
  eventId?: string;
}

// R2.8 (30.08.2026): Antwortform von GET /api/customers/:id/invoices — Spiegelbild des
// event-bezogenen Pendants (GET /api/events/:id/invoices, routes/invoices.ts).
interface CustomerInvoiceRow {
  id: string;
  eventId: string;
  eventOccasion: string | null;
  invoiceType: string;
  status: string;
  invoiceNumber: string | null;
  amountGrossCents: number;
  sentAt: string | null;
  createdAt: string;
  pdfUrl: string | null;
}

const INVOICE_STATUS_LABEL: Record<string, string> = {
  draft: "Entwurf",
  open: "Offen",
  paid: "Bezahlt",
  voided: "Storniert",
  voiding: "Wird storniert",
};

const HISTORY_FILTERS: { key: "all" | HistoryCategory; label: string }[] = [
  { key: "all", label: "Alles" },
  { key: "anfrage", label: "Anfragen" },
  { key: "angebot", label: "Angebote" },
  { key: "event", label: "Events" },
  { key: "zahlung", label: "Zahlungen" },
];

/**
 * H6: Notizen mit Autor/Datum.
 *
 * Migration 335 (M-46-Revision, 30.08.2026): Notizen leben jetzt als eigene, unveraenderliche
 * Zeilen in `customer_internal_notes` (GET/POST `/api/customers/:id/notes`) - jede Zeile traegt
 * bereits `actorLabel`/`createdAt`, kein Parsen mehr fuer NEUE Eintraege noetig.
 *
 * `parseNotes`/`NOTE_RE` bleiben trotzdem: die Migration hat den kompletten Vor-335-Bestand von
 * `customers.internalNotes` als GENAU EINE Zeile ("Altbestand (migriert …)") uebernommen, deren
 * Text weiterhin das alte "[Datum · Autor] Text"-Praefixformat traegt (mehrere Eintraege durch
 * Leerzeile getrennt). `noteEntriesFromRow` wendet dieselbe Parse-Logik wie bisher NUR auf diese
 * eine Alt-Zeile an und spaltet sie so weiterhin in einzelne, hübsch dargestellte Eintraege auf -
 * mit ihrem jeweils urspruenglichen Autor/Datum, falls das Praefix passt. Fuer eine normale
 * (neue) Zeile ohne Praefix liefert der Parser genau EINEN Eintrag mit leerem author/date, und
 * `noteEntriesFromRow` faellt dann auf die echten Spalten (actorLabel/createdAt) zurueck.
 */
interface NoteEntry {
  author: string;
  date: string;
  text: string;
}
const NOTE_RE = /^\[(.+?) · (.+?)\]\s*([\s\S]*)$/;
function parseNotes(raw: string | null): NoteEntry[] {
  if (!raw?.trim()) return [];
  return raw
    .split(/\n{2,}(?=\[)/)
    .map((block) => {
      const m = NOTE_RE.exec(block.trim());
      return m ? { date: m[1]!, author: m[2]!, text: m[3]!.trim() } : { date: "", author: "", text: block.trim() };
    })
    .filter((n) => n.text);
}
/** Eine DB-Zeile (`CustomerNote`) -> ein oder mehrere Anzeige-Eintraege (siehe Kommentar oben). */
function noteEntriesFromRow(row: CustomerNote): NoteEntry[] {
  return parseNotes(row.note).map((p) => ({
    author: p.author || row.actorLabel || "Unbekannt",
    date: p.date || formatDate(row.createdAt),
    text: p.text,
  }));
}

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Punkt 115 (G2): Links von hier auf einen Vorgang tragen die Herkunft („← Zurück zu <Kunde>“).
  const mitHier = useMitHerkunft();
  const customerQ = useApi<Customer>(id ? `/api/customers/${id}` : null);
  const eventsQ = useApi<EventRow[]>("/api/events");
  // Kostenuebernahme-Sichtbarkeit (Domenico/STORIA-Feedback, 07.09.2026): dieselbe dauerhafte
  // Kennzeichnung wie in events.tsx (~Zeile 75-79), dieselbe Datenquelle.
  const signiertStatusQ = useApi<{ eventId: string; signedAt: string }[]>("/api/cost-acceptances/signiert-status");
  const kostenuebernahmeSigniert = useMemo(
    () => new Set((signiertStatusQ.data ?? []).map((r) => r.eventId)),
    [signiertStatusQ.data],
  );
  // Migration 335 (M-46-Revision): die Notizen-Historie kommt jetzt aus einer eigenen Route.
  const notesQ = useApi<CustomerNote[]>(id ? `/api/customers/${id}/notes` : null);
  const customer = customerQ.data;
  // Datenanreicherung Welle B/C: Mandanten-Schalter + gecachtes Lookup-Ergebnis (null ohne
  // Firma/ohne abgeschlossenen Hintergrund-Job bzw. ohne manuellen Google-Places-Abruf).
  const settingsQ = useApi<{ handelsregisterEnabled?: boolean; googlePlacesEnabled?: boolean } | null>("/api/settings/payment-terms");
  const handelsregisterEnabled = settingsQ.data?.handelsregisterEnabled ?? false;
  const googlePlacesEnabled = settingsQ.data?.googlePlacesEnabled ?? false;
  const hrQ = useApi<HandelsregisterLookup | null>(id ? `/api/customers/${id}/handelsregister` : null);
  const placesQ = useApi<GooglePlacesLookup | null>(id ? `/api/customers/${id}/places-lookup` : null);
  // R3.2: EINE Antwort mit allem, was die Karte braucht (Zeile, Nummern, Modul-Zustand). Rein
  // lesend — dieser Aufruf beruehrt keine Behoerde.
  const ustidQ = useApi<UstIdAntwort | null>(id ? `/api/customers/${id}/ustid` : null);
  // R2.8 (30.08.2026): kundenuebergreifende Rechnungsuebersicht (bisher nur pro Vorgang sichtbar).
  const invoicesQ = useApi<CustomerInvoiceRow[]>(id ? `/api/customers/${id}/invoices` : null);
  // Abrechnung Stufe 1 (Antoine 28.09.2026): Guthaben beim Kunden aus einer Überzahlung.
  const guthabenQ = useApi<{ saldoCents: number; buchungen: { id: string; art: string; betragCents: number; begruendung: string | null; eventId: string | null; am: string }[] }>(id ? `/api/customers/${id}/guthaben` : null);
  // Dedup-Plan Phase 2 (30.08.2026): das Zusammenfuehren-Gate. Inhaber duerfen mergen
  // (Antoine-Entscheidung 30.08.2026) — Staff sieht die Aktion gar nicht erst; die Route selbst
  // erzwingt dieselbe Grenze serverseitig (403 forbidden_role).
  const meQ = useApi<Me>("/api/me");
  const membership = currentMembership(meQ.data);
  const istInhaber = membership?.role === "owner";
  const [mergeOffen, setMergeOffen] = useState(false);
  /**
   * Dedup-Plan Phase 4 (30.08.2026): `?merge=<kundeId>` — das Dubletten-Review auf der
   * Kundenliste verlinkt hierher und bringt den ANDEREN Kunden des Kandidatenpaars mit. Der
   * Drawer oeffnet sich dann mit fertiger Gegenueberstellung; Richtung, Bestaetigung und der
   * eigentliche Merge bleiben exakt der bestehende Phase-2-Fluss (nichts wird vorbestaetigt).
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const mergeVorauswahlId = searchParams.get("merge");
  useEffect(() => {
    if (mergeVorauswahlId && istInhaber && customer && !customer.mergedIntoId) setMergeOffen(true);
  }, [mergeVorauswahlId, istInhaber, customer?.id, customer?.mergedIntoId]);

  // Phase B (Datenanreicherung): Der Agent meldet nach einem places_lookup bzw. apply_enrichment
  // changed:{ resource:"customer", id }. Nur wenn die geänderte id dem gerade offenen Kunden
  // entspricht, laden wir gezielt die Kundendaten (Kontaktkarte) UND den gecachten Places-Vorschlag
  // (appliedFields/„✓ übernommen") neu — so wird das übernommene Feld sofort sichtbar, ohne Voll-Reload.
  useContentRefetch(
    "customer",
    () => {
      customerQ.reload();
      placesQ.reload();
    },
    id,
  );

  const events = useMemo(() => (eventsQ.data ?? []).filter((e) => e.customerId === id), [eventsQ.data, id]);
  const eventIds = events.map((e) => e.id).join(",");

  // R2.7 (30.08.2026): "Kunde anschreiben" soll ueber MAESTROs eigenes Mail-System laufen
  // (Posteingang) statt das lokale Mailprogramm des Betreibers zu oeffnen - der Composer dort
  // haengt an einem Vorgang, deshalb hier den neuesten NICHT stornierten Vorgang vorauswaehlen.
  // Ohne passenden Vorgang bleibt mailto: der einzig moegliche Weg (kein Vorgang, kein Composer).
  const neuesterAktiverVorgang = useMemo(
    () =>
      [...events]
        .filter((e) => e.status !== "cancelled")
        .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0] ?? null,
    [events],
  );

  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  useEffect(() => {
    if (events.length === 0) {
      setPayments([]);
      setPaymentsError(null);
      return;
    }
    let alive = true;
    (async () => {
      // A-12: zusaetzlich zu HTTP-Fehlern (failed++) auch GEWORFENE Fehler (Netzabbruch,
      // json()-Parsefehler) abfangen - sonst rejected Promise.all unbehandelt und die
      // Offenen-Posten/KPIs blieben ohne Hinweis leer.
      try {
        let failed = 0;
        const lists = await Promise.all(
          events.map(async (e) => {
            const res = await apiFetch(`/api/events/${e.id}/payments`);
            if (!res.ok) {
              failed++;
              return [] as Payment[];
            }
            const body = (await res.json()) as { data: Payment[] };
            return body.data;
          }),
        );
        if (alive) {
          setPayments(lists.flat());
          // N-5: Teil-Fehler beim Fan-out sichtbar machen (KPIs/Historie sonst still unvollständig).
          setPaymentsError(failed > 0 ? `${failed} von ${events.length} Vorgängen konnten nicht geladen werden – Zahlen ggf. unvollständig.` : null);
        }
      } catch (err) {
        if (alive) setPaymentsError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIds]);

  const [historyFilter, setHistoryFilter] = useState<"all" | HistoryCategory>("all");
  /**
   * Migration 245 — das Formular zum Ändern eines BESTEHENDEN Kunden.
   *
   * Bis hierher gab es nur „Kunde anlegen" (`pages/customers.tsx`). Das war nicht nur eine Lücke
   * im Bedienkomfort: § 4.2 sieht bei „kein Eintrag gefunden" die Handlung „Anderen Namen
   * versuchen" vor, und die hatte deshalb kein Ziel — `@maestro/anreicherung` gab dort
   * ausdrücklich `null` zurück, „keine Schaltfläche ohne Wirkung". Erst mit diesem Formular
   * bekommen `kein_treffer` und `verworfen` ihre Handlung.
   *
   * `bearbeitenFokus` sagt, WELCHES Feld beim Öffnen den Fokus bekommt: kommt der Klick aus einer
   * Recherche-Karte, ist die Firma gemeint und nicht der Name.
   */
  const [bearbeitenOffen, setBearbeitenOffen] = useState(false);
  const [bearbeitenFokus, setBearbeitenFokus] = useState<"name" | "company">("name");
  const firmaBearbeiten = () => {
    setBearbeitenFokus("company");
    setBearbeitenOffen(true);
  };
  const [noteDraft, setNoteDraft] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  // R2.9 (30.08.2026): Checkout-Vorgaenge (source==='checkout') erreichen nie status='paid'/
  // 'completed' (Migration 135/343, bewusst) - ohne diese Ergaenzung zaehlt die Umsatz-Kachel
  // bezahlte Website-Bestellungen nie mit. `checkoutDeckung` kommt fertig aus GET /api/events
  // (lib/payment-coverage.ts#leseDeckung, dieselbe Rechenstelle wie ueberall sonst im System).
  // Gezaehlt wird der TATSAECHLICH eingegangene Betrag (bezahltCents), nicht amountTotalCents -
  // bei einer Teilzahlung ist das der bereits eingegangene Teil, kein geratener Vollbetrag.
  const istBezahltesCheckoutEvent = (e: EventRow) => e.source === "checkout" && (e.checkoutDeckung?.bezahltCents ?? 0) > 0;
  const lifetimeCents =
    events.filter((e) => ["paid", "completed"].includes(e.status)).reduce((s, e) => s + (e.amountTotalCents ?? 0), 0) +
    events.filter(istBezahltesCheckoutEvent).reduce((s, e) => s + (e.checkoutDeckung?.bezahltCents ?? 0), 0);
  const avgOrderCents = events.length > 0 ? Math.round(lifetimeCents / events.length) : 0;
  // F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md D2/L7): "Anfrage" = Phase vor der Zusage.
  // `openInquiries` (Anfragen ohne Vorgang, per E-Mail zugeordnet) ist entfallen — seit Migration
  // 412 hat jede Anfrage sofort einen Vorgang, die Phase ergibt sich direkt aus `events`.
  const inquiryCount = events.filter(
    (e) => ANFRAGE_PHASE_STATUS.has(e.status) || (e.status === "cancelled" && e.wonAt == null),
  ).length;
  const openBalanceCents = payments.filter((p) => ["pending", "processing"].includes(p.status)).reduce((s, p) => s + p.amountCents, 0);

  const isRegular = events.filter((e) => ["paid", "completed"].includes(e.status) || istBezahltesCheckoutEvent(e)).length >= 2;
  const customerSinceLabel = customer ? new Date(customer.createdAt).toLocaleDateString("de-DE", { month: "long", year: "numeric" }) : "";

  // #1515 H2: „Rest offen X €“ nach abgeschlossener Abrechnung, je Vorgang.
  const restOffenJeVorgang = useMemo(() => new Map(events.flatMap((e) => { const m = zahlungsstandMeta(e); return m ? [[e.id, m] as const] : []; })), [events]);
  const history = useMemo<HistoryEntry[]>(() => {
    const entries: HistoryEntry[] = [];
    // F2: der fruehere Zweig ueber `openInquiries` (per E-Mail zugeordnete Anfragen OHNE Vorgang)
    // ist entfallen — seit Migration 412 hat jede Anfrage sofort einen Vorgang und ist damit
    // bereits ueber den `created-${e.id}`-Eintrag im Events-Zweig unten abgedeckt.
    for (const e of events) {
      const sm = eventStatusMeta(e.status); // N-11: unbekannter Status crasht die Historie nicht
      entries.push({
        id: `created-${e.id}`,
        at: e.createdAt,
        category: "anfrage",
        icon: "mail",
        title: `Anfrage eingegangen — ${anlassAnzeige(e.occasion) ?? "Vorgang"}`,
        detail: [e.guestCount ? `${e.guestCount} Gäste` : null, e.location].filter(Boolean).join(" · "),
        // CX-Audit 2026-07-17 (P1): Ohne href waren laufende Vorgänge (inquiry/offer_draft/
        // no_response ohne offerSentAt) vom Kundenprofil aus ÜBERHAUPT nicht erreichbar —
        // der einzige Weg führte über den Umweg /vorgaenge. Jetzt öffnet der Eintrag
        // den Vorgang direkt.
        href: vorgangPfad(e.id),
        eventId: e.id,
      });
      if (e.offerSentAt) {
        entries.push({
          id: `offer-${e.id}`,
          at: e.offerSentAt,
          category: "angebot",
          icon: "send",
          title: "Angebot gesendet",
          detail: [anlassAnzeige(e.occasion), e.guestCount ? `${e.guestCount} Gäste` : null, e.location, money(e.amountTotalCents)].filter(Boolean).join(" · "),
          pill: { label: sm.label, cls: PILL_CLASSES[sm.tone] },
          href: angebotPfad(e.id),
        });
      }
      if (["completed", "cancelled", "paid"].includes(e.status)) {
        entries.push({
          id: `event-${e.id}`,
          at: e.eventDate ?? e.createdAt,
          category: "event",
          icon: "event",
          title: `${anlassAnzeige(e.occasion) ?? "Vorgang"} ${e.status === "cancelled" ? "storniert" : "durchgeführt"}`,
          detail: [e.guestCount ? `${e.guestCount} Gäste` : null, e.location, money(e.amountTotalCents)].filter(Boolean).join(" · "),
          pill: { label: sm.label, cls: PILL_CLASSES[sm.tone] },
          href: vorgangPfad(e.id),
        });
      } else if (istBezahltesCheckoutEvent(e)) {
        // R2.9: eigener Eintrag statt des status-basierten oben — der Vorgangsstatus bleibt bei
        // Checkout-Events immer "offer_chosen" (nie "paid"), ein Pill daraus waere irrefuehrend.
        entries.push({
          id: `event-${e.id}`,
          at: e.eventDate ?? e.createdAt,
          category: "event",
          icon: "event",
          title: `${anlassAnzeige(e.occasion) ?? "Bestellung"} — über Website-Checkout bezahlt`,
          detail: [e.guestCount ? `${e.guestCount} Gäste` : null, e.location, money(e.checkoutDeckung?.bezahltCents ?? 0)].filter(Boolean).join(" · "),
          pill: { label: e.checkoutDeckung?.gedeckt ? "Bezahlt" : "Teilweise bezahlt", cls: PILL_CLASSES.success },
          href: vorgangPfad(e.id),
        });
      }
    }
    for (const p of payments) {
      if (!["succeeded", "refunded"].includes(p.status)) continue;
      const meta = PAYMENT_STATUS[p.status];
      entries.push({
        id: `pay-${p.id}`,
        at: p.paidAt ?? p.createdAt,
        category: "zahlung",
        icon: "payments",
        title: p.status === "refunded" ? "Erstattung gebucht" : "Zahlung eingegangen",
        // Punkt 38 (Migration 484): eine Teil-Rueckerstattung steht dazu — sonst sieht es nach dem
        // vollen Betrag aus, obwohl nur der Rest als bezahlt gilt.
        detail: [
          money(p.amountCents),
          p.status === "succeeded" && (p.refundedAmountCents ?? 0) > 0 ? `davon ${money(p.refundedAmountCents ?? 0)} erstattet` : null,
          PAYMENT_TYPE_LABEL[p.paymentType] ?? p.paymentType,
        ].filter(Boolean).join(" · "),
        pill: meta ? { label: meta.label, cls: PILL_CLASSES[meta.tone] } : undefined,
        // CX-Audit 2026-07-17: Zahlung trägt ihre eventId — Klick öffnet den zugehörigen
        // Vorgang mit der Abrechnungskarte (vorher toter Eintrag).
        href: vorgangPfad(p.eventId),
      });
    }
    return entries.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [events, payments]);

  const filteredHistory = historyFilter === "all" ? history : history.filter((h) => h.category === historyFilter);

  // Migration 335 (M-46-Revision): EIN INSERT statt read-modify-write auf einem Textfeld - kein
  // Konfliktfall mehr moeglich, also auch kein 409/notes_conflict-Sonderpfad mehr noetig.
  async function addNote() {
    if (!id || !noteDraft.trim()) return;
    setNoteBusy(true);
    setNoteError(null);
    try {
      await apiMutate(`/api/customers/${id}/notes`, "POST", { note: noteDraft.trim() });
      setNoteDraft("");
      notesQ.reload();
    } catch (err) {
      setNoteError(err instanceof Error ? err.message : String(err));
    } finally {
      setNoteBusy(false);
    }
  }

  if (customerQ.loading) return <LoadingRows rows={6} />;
  if (customerQ.error) return <ErrorNote message={customerQ.error} />;
  if (!customer) return <EmptyState icon="person_off" title="Kunde nicht gefunden" />;

  // Reihenfolge: Zeilen kommen bereits neueste-zuerst von der API; jede Zeile kann (Altbestand)
  // in mehrere Anzeige-Eintraege zerfallen, siehe noteEntriesFromRow().
  const notes = (notesQ.data ?? []).flatMap(noteEntriesFromRow);
  // PR4 (Adressmodell, WARTESCHLANGE Punkt 7): jetzt inkl. Adresszusatz + lesbarem Landnamen
  // (Migration 422) — Name/Firma bleiben hier bewusst aussen vor, sie stehen an dieser Stelle
  // bereits ueber der Kontaktkarte (Breadcrumb) bzw. in der Handelsregister-Karte darunter.
  const address = adresseEinzeilig({
    street: customer.addressStreet,
    addition: customer.addressAddition,
    zip: customer.addressZip,
    city: customer.addressCity,
    countryCode: customer.addressCountryCode,
  });

  return (
    <>
      {/* H4: Breadcrumb statt Drawer-Titel — seit Punkt 115 der gemeinsame `Rueckweg`. */}
      <Rueckweg className="mb-4" eltern={{ label: "Kunden", to: "/kunden" }} aktuell={customer.name} />

      {/* Dedup-Plan Phase 2: der Grabstein erklaert sich selbst und fuehrt zum lebenden Ziel —
          eine kommentarlose, halb leere Kundenseite waere die Sorte stiller Zustand, gegen die
          R0.7 steht. */}
      {customer.mergedIntoId && (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-outline-variant/60 bg-surface-container-low px-4 py-3 dark:border-outline/40 dark:bg-surface-variant/15">
          <Icon name="merge" className="text-[20px] text-secondary" />
          <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
            Dieser Kunde wurde mit einem anderen Kunden <strong>zusammengeführt</strong> — alle Vorgänge und Notizen liegen beim Ziel-Kunden.
          </p>
          <ButtonRouterLink to={`/kunden/${customer.mergedIntoId}`} variant="secondary" className="!py-1.5">
            Zum Ziel-Kunden <Icon name="arrow_forward" className="text-[15px]" />
          </ButtonRouterLink>
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-full bg-primary/14 text-[17px] font-bold text-primary">
            {initials(customer.name)}
          </div>
          <div>
            {/* H7: Badges */}
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-[25px] font-bold leading-tight tracking-[-0.02em] text-on-background dark:text-inverse-on-surface">{customer.name}</h1>
              {/* Punkt 42: die Pille folgt dem Kundentyp-Feld statt dem Firmenfeld. */}
              {kundentypVon(customer) !== "privat" && (
                <span className={`rounded-full px-2.5 py-1 font-label-md text-[11px] font-semibold ${PILL_CLASSES.neutral}`}>
                  {kundentypVon(customer) === "verein" ? "Verein" : "Firmenkunde"}
                </span>
              )}
              {isRegular && (
                <span className={`rounded-full px-2.5 py-1 font-label-md text-[11px] font-semibold ${PILL_CLASSES.success}`}>Stammkunde</span>
              )}
            </div>
            <p className="mt-1 text-[13px] text-secondary dark:text-secondary-fixed-dim">
              {[customer.company, `Kundin/Kunde seit ${customerSinceLabel}`].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          {/* Migration 245: der Weg, den es bis dahin nicht gab. Bewusst hier oben und nicht in
              einer Recherche-Karte versteckt — geändert werden hier ALLE Stammdaten, die
              Recherche ist nur der häufigste Anlass. */}
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setBearbeitenFokus("name");
              setBearbeitenOffen(true);
            }}
          >
            <Icon name="edit" className="text-[16px]" /> Bearbeiten
          </Button>
          {/* Dedup-Plan Phase 2: Merge nur fuer Inhaber und nie AUF einem Grabstein. */}
          {istInhaber && !customer.mergedIntoId && (
            <Button type="button" variant="secondary" onClick={() => setMergeOffen(true)}>
              <Icon name="merge" className="text-[16px]" /> Zusammenführen
            </Button>
          )}
          {neuesterAktiverVorgang ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/posteingang?eventId=${neuesterAktiverVorgang.id}`)}
            >
              <Icon name="mail" className="text-[16px]" /> Kunde anschreiben
            </Button>
          ) : (
            <ButtonLink
              href={`mailto:${customer.email}`}
              variant="secondary"
              title="Kein laufender Vorgang gefunden - Nachricht öffnet das lokale Mailprogramm statt des Posteingangs."
            >
              <Icon name="mail" className="text-[16px]" /> Kunde anschreiben
            </ButtonLink>
          )}
          {/* F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md): fuehrt seit dem Umbau in den
              Anfragen-Anlege-Drawer (POST /api/inquiries) statt direkt in den Buchungen-Drawer —
              ein neu angelegter Fall braucht eine Anfrage-Protokollzeile, sonst taucht er in
              keiner Liste auf (s. Kommentar am Drawer in pages/buchungen.tsx). */}
          <Button type="button" onClick={() => navigate(`${ANFRAGEN_PFAD}?neu=1&kunde=${customer.id}`)}>
            <Icon name="add" className="text-[16px]" /> Angebot erstellen
          </Button>
        </div>
      </div>

      {/* CX-Audit 2026-07-17: KPI-Kacheln beantworten ihre Frage per Klick — „Vorgänge"
          filtert die Historie unten, „Offene Posten" springt in die Zahlungsübersicht mit
          vorbefüllter Kundensuche. */}
      <div className="mb-5 grid grid-cols-2 gap-3.5 md:grid-cols-4">
        <KpiCard label="Umsatz gesamt" value={money(lifetimeCents)} icon="payments" />
        <button
          type="button"
          className="text-left"
          title="Historie nach Vorgängen filtern"
          onClick={() => {
            setHistoryFilter("event");
            document.getElementById("kunden-historie")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        >
          <KpiCard
            label="Vorgänge"
            value={events.length}
            icon="event"
            delta={inquiryCount > 0 ? { text: `+${inquiryCount} angefragt`, tone: "warn" } : undefined}
          />
        </button>
        <KpiCard label="Ø Auftragswert" value={money(avgOrderCents)} icon="calculate" />
        <KpiCard
          label="Offene Posten"
          value={money(openBalanceCents)}
          icon="account_balance_wallet"
          // Punkt 115 (Paket 1): nur bei offenem Betrag auf den Reiter „Offen“ — sonst stand man
          // (Anlass Debora Ribeiro) in einem leeren Reiter, waehrend die Zahlung unter „Bezahlt“ lag.
          to={zahlungenPfad({
            filter: openBalanceCents > 0 ? "open" : undefined,
            q: customer.name,
            von: `/kunden/${customer.id}`,
            vonName: customer.name,
          })}
          delta={openBalanceCents > 0 ? { text: "offen", tone: "warn" } : undefined}
        />
      </div>

      {paymentsError && <div className="mb-5"><ErrorNote message={paymentsError} /></div>}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_1.5fr]">
        <div className="flex flex-col gap-4">
          {/* R1.3 — Die eigene Historie ist der ERSTE Block, vor allen Fremddaten (§ 2.5, P-7:
              was wir selbst erlebt haben, schlägt jede Registerauskunft). Die Reihenfolge ist
              hier der Beweis und wird in apps/api/test/kundenhistorie-anzeige.test.ts über die
              Positionen im Quelltext festgehalten — Handelsregister, Bundesanzeiger und Google
              kommen alle darunter. Derselbe Block wie im Anfragen-Drawer (R1.2), dieselbe Regel,
              dieselben Sätze; nur die Überschrift unterscheidet sich. */}
          {id && <KundenhistorieBlock pfad={`/api/customers/${id}/historie`} ort="kunden_profil" />}

          {/* Punkt 42: Kundentyp (Privat · Firma · Verein) — hier wird er gesetzt und bestätigt. */}
          <KundentypKarte kunde={customer} onGeaendert={customerQ.reload} />

          {/* Kontakt */}
          <Card>
            <CardRow title="Kontakt" />
            <div className="flex flex-col gap-3 px-[18px] py-[14px]">
              {neuesterAktiverVorgang ? (
                <button
                  type="button"
                  onClick={() => navigate(`/posteingang?eventId=${neuesterAktiverVorgang.id}`)}
                  className="flex items-center gap-2.5 text-left text-[12.5px] font-semibold text-on-background hover:text-primary dark:text-inverse-on-surface"
                >
                  <Icon name="mail" className="text-[16px] text-secondary" /> <span className="truncate">{customer.email}</span>
                </button>
              ) : (
                <a
                  href={`mailto:${customer.email}`}
                  className="flex items-center gap-2.5 text-[12.5px] font-semibold text-on-background hover:text-primary dark:text-inverse-on-surface"
                >
                  <Icon name="mail" className="text-[16px] text-secondary" /> <span className="truncate">{customer.email}</span>
                </a>
              )}
              {customer.phone && (
                <div className="flex items-center gap-2.5 text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                  <Icon name="call" className="text-[16px] text-secondary" /> {customer.phone}
                </div>
              )}
              {address && (
                <div className="flex flex-wrap items-center gap-2 text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                  <span className="flex items-center gap-2.5">
                    <Icon name="location_on" className="text-[16px] text-secondary" /> {address}
                  </span>
                  <StatusPill
                    meta={
                      landChip({
                        street: customer.addressStreet,
                        zip: customer.addressZip,
                        city: customer.addressCity,
                        countryCode: customer.addressCountryCode,
                        abgeleitetAm: customer.addressCountryAbgeleitetAm,
                      }) ?? undefined
                    }
                  />
                </div>
              )}
              {customer.taxNumber && (
                <div className="flex items-center gap-2.5 text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                  <Icon name="receipt_long" className="text-[16px] text-secondary" /> {customer.taxNumber}
                </div>
              )}
              <KundenSprache kundeId={customer.id} sprache={customer.sprache ?? null} onGeaendert={customerQ.reload} />
            </div>
          </Card>

          {/* R3.4 + R4.4 — Widersprüche werden GEZEIGT, nicht aufgelöst (§ 3.4). Steht direkt
              unter der Kontaktkarte, weil es genau deren Anschrift betrifft: wer sie liest, soll
              im selben Blick sehen, dass eine andere Quelle etwas anderes sagt. Die Karte
              erscheint nur, wenn es wirklich auseinandergeht — die ganze Entscheidung liegt in
              der geprüften Regel (@maestro/quellenabgleich), hier wird nur gerendert. */}
          <AdressenNebeneinander customer={customer} hrLookup={hrQ.data ?? null} placesLookup={placesQ.data ?? null} />

          {/* Handelsregister + Bundesanzeiger + KI-Kurzzusammenfassung (Datenanreicherung
              Welle B) — bei gesetzter Firma. R0.7: der Mandanten-Schalter versteckt die Karte
              NICHT mehr, sondern ist einer der neun Zustände („Modul ist ausgeschaltet." +
              Sprung in die Kachel, § 4.2). Eine verschwundene Karte erklärt nichts. */}
          {customer.company && (
            <HandelsregisterCard
              customerId={customer.id}
              company={customer.company}
              modulAktiv={handelsregisterEnabled}
              lookup={hrQ.data}
              // Solange die Modul-Einstellungen laden, ist `handelsregisterEnabled` noch false —
              // ohne dieses `||` behauptete die Karte für einen Wimpernschlag „Modul ist
              // ausgeschaltet." Ein Zustand, den man erst glauben darf, wenn er feststeht.
              loading={hrQ.loading || settingsQ.loading}
              onReload={hrQ.reload}
              onFirmaBearbeiten={firmaBearbeiten}
            />
          )}

          {/* R3.2: Die USt-IdNr.-Bestätigung (§ 18e UStG). Wie die Handelsregister-Karte bleibt
              sie auch bei ausgeschaltetem Modul stehen — eine verschwundene Karte erklärt nichts. */}
          {customer.company && (
            <UstIdCard
              customerId={customer.id}
              company={customer.company}
              antwort={ustidQ.data ?? null}
              loading={ustidQ.loading}
              onReload={() => {
                ustidQ.reload();
                customerQ.reload();
              }}
            />
          )}

          {/* Google Places (Datenanreicherung Welle C) — manueller Lookup-Button, gated auf den
              Mandanten-Schalter (die Route selbst gated zusaetzlich auf GOOGLE_PLACES_API_KEY,
              siehe settings.tsx SystemTab-Health-Flag "places"). */}
          {googlePlacesEnabled && (
            <GooglePlacesCard
              customerId={customer.id}
              company={customer.company}
              addressStreet={customer.addressStreet}
              addressZip={customer.addressZip}
              addressCity={customer.addressCity}
              lookup={placesQ.data}
              loading={placesQ.loading}
              onReload={placesQ.reload}
              onCustomerUpdated={customerQ.reload}
              onFirmaBearbeiten={firmaBearbeiten}
            />
          )}

          {/* R2.8 (30.08.2026): kundenuebergreifende Rechnungsuebersicht — bisher gab es
              Rechnungen nur pro einzelner Veranstaltung zu sehen (dort weiterhin unter
              "Abrechnung"), additiv, verdraengt nichts Bestehendes. */}
          <Card>
            <CardRow title="Rechnungen" />
            <div className="flex flex-col gap-2.5 px-[18px] py-[14px]">
              {invoicesQ.loading ? (
                <LoadingRows rows={2} />
              ) : invoicesQ.error ? (
                <ErrorNote message={invoicesQ.error} />
              ) : !invoicesQ.data || invoicesQ.data.length === 0 ? (
                <p className="text-[12.5px] text-secondary-fixed-dim">Noch keine Rechnungen.</p>
              ) : (
                invoicesQ.data.map((inv) => (
                  <Link
                    key={inv.id}
                    to={mitHier(vorgangPfad(inv.eventId), customer.name)}
                    className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-[12.5px] hover:bg-surface-container-high dark:hover:bg-white/[0.03]"
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-semibold text-on-background dark:text-inverse-on-surface">
                        {inv.invoiceNumber ?? "Entwurf"} {inv.invoiceType === "credit" ? "(Gutschrift)" : ""}
                        {inv.eventOccasion ? ` · ${anlassAnzeige(inv.eventOccasion)}` : ""}
                      </span>
                      <span className="text-secondary-fixed-dim">
                        {inv.sentAt ? `gesendet ${formatDate(inv.sentAt)}` : `angelegt ${formatDate(inv.createdAt)}`}
                        {" · "}
                        {inv.status === "open" && inv.amountGrossCents < 0 && inv.invoiceType !== "credit" ? "zu Gunsten des Gastes" : (INVOICE_STATUS_LABEL[inv.status] ?? inv.status)}
                      </span>
                    </span>
                    <span className="shrink-0 font-semibold text-on-background dark:text-inverse-on-surface">{money(inv.amountGrossCents)}</span>
                  </Link>
                ))
              )}
            </div>
          </Card>

          {guthabenQ.data && guthabenQ.data.buchungen.length > 0 && (
            <Card data-testid="kunden-guthaben">
              <CardRow title="Guthaben" />
              <div className="flex flex-col gap-2 px-[18px] py-[14px] text-[12.5px]">
                <p className="flex items-baseline justify-between">
                  <span className="text-secondary-fixed-dim">Verfügbar, wird bei der nächsten Abrechnung verrechnet</span>
                  <span className="text-[15px] font-bold text-primary">{money(guthabenQ.data.saldoCents)}</span>
                </p>
                {guthabenQ.data.buchungen.map((b) => (
                  <p key={b.id} className="flex items-start justify-between gap-3">
                    <span className="min-w-0">
                      <span className="font-semibold text-on-background dark:text-inverse-on-surface">{b.art === "ueberzahlung" ? "Aus Überzahlung" : "Verrechnet"}</span>
                      <span className="block text-secondary-fixed-dim">{formatDate(b.am)}{b.begruendung ? ` · ${b.begruendung}` : ""}</span>
                    </span>
                    <span className="shrink-0 font-semibold tabular-nums">{money(b.betragCents)}</span>
                  </p>
                ))}
              </div>
            </Card>
          )}

          {/* H6: Notizen mit Autor/Datum + Eingabe */}
          <Card>
            <CardRow title="Notizen" />
            <div className="flex flex-col gap-3 px-[18px] py-[14px]">
              {notes.length === 0 ? (
                <p className="text-[12.5px] text-secondary-fixed-dim">Noch keine Notizen.</p>
              ) : (
                notes.map((n, idx) => (
                  <div key={idx} className="flex gap-2.5">
                    <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full bg-primary/14 text-[10px] font-bold text-primary">
                      {n.author ? initials(n.author) : "?"}
                    </span>
                    <div>
                      <p className="text-[12.5px] leading-[1.55] text-on-background dark:text-inverse-on-surface">{n.text}</p>
                      {(n.author || n.date) && (
                        <p className="mt-0.5 text-[10.5px] text-secondary-fixed-dim">{[n.author, n.date].filter(Boolean).join(" · ")}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
              {noteError && <ErrorNote message={noteError} />}
              <div className="flex items-center gap-2">
                <Input
                  className="!py-2 text-[12.5px]"
                  placeholder="Notiz hinzufügen …"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void addNote();
                  }}
                  disabled={noteBusy}
                />
                <Button type="button" variant="secondary" className="shrink-0 !py-2" disabled={noteBusy || !noteDraft.trim()} onClick={() => void addNote()}>
                  {noteBusy ? "…" : "Hinzufügen"}
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* H5: gemischte Historie. id: Scroll-Ziel der KPI-Kachel „Veranstaltungen". */}
        <Card id="kunden-historie" className="scroll-mt-20">
          <CardRow
            title="Historie"
            actions={HISTORY_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setHistoryFilter(f.key)}
                className={`rounded-full px-2.5 py-1 font-label-md text-[11px] font-semibold ${
                  historyFilter === f.key
                    ? "bg-on-background text-inverse-on-surface dark:bg-inverse-on-surface dark:text-inverse-surface"
                    : "border border-outline-variant/50 text-on-surface-variant dark:border-outline/40 dark:text-secondary-fixed-dim"
                }`}
              >
                {f.label}
              </button>
            ))}
          />
          <div className="px-[18px] py-4">
            {eventsQ.loading ? (
              <LoadingRows rows={4} />
            ) : filteredHistory.length === 0 ? (
              <EmptyState icon="history" title="Noch keine Einträge" hint="Anfragen, Angebote, Events und Zahlungen erscheinen hier." />
            ) : (
              <ul>
                {filteredHistory.map((h, idx) => (
                  <li key={h.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/12 text-primary">
                        <Icon name={h.icon} className="text-[14px]" />
                      </span>
                      {idx < filteredHistory.length - 1 && <span className="w-px flex-1 bg-outline-variant/40 dark:bg-outline/30" />}
                    </div>
                    <div className={idx < filteredHistory.length - 1 ? "flex-1 pb-4" : "flex-1"}>
                      <div className="flex flex-wrap items-center gap-2">
                        {h.href ? (
                          <Link to={mitHier(h.href, customer.name)} className="text-[13px] font-semibold text-on-background hover:text-primary dark:text-inverse-on-surface">
                            {h.title}
                          </Link>
                        ) : (
                          <span className="text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{h.title}</span>
                        )}
                        {h.pill && <span className={`rounded-full px-2 py-0.5 font-label-md text-[10.5px] font-semibold ${h.pill.cls}`}>{h.pill.label}</span>}
                        {h.eventId && restOffenJeVorgang.get(h.eventId) && <StatusPill meta={restOffenJeVorgang.get(h.eventId)!} testId="rest-offen" />}
                        {h.eventId && kostenuebernahmeSigniert.has(h.eventId) && (
                          <StatusPill meta={{ label: "🧾 Kostenübernahme signiert", tone: "success" }} />
                        )}
                        <span className="ml-auto text-[11px] text-secondary-fixed-dim">{formatDate(h.at)}</span>
                      </div>
                      {h.detail && <p className="mt-0.5 text-[11.5px] text-secondary dark:text-secondary-fixed-dim">{h.detail}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>

      {/* Migration 245: das Formular, das es bis dahin nicht gab. */}
      <KundeBearbeiten
        offen={bearbeitenOffen}
        kunde={customer}
        fokus={bearbeitenFokus}
        onClose={() => setBearbeitenOffen(false)}
        onGespeichert={() => {
          setBearbeitenOffen(false);
          customerQ.reload();
          // Eine geänderte Firma erzeugt eine ANDERE Registerzeile (der Schlüssel ist der
          // normalisierte Name) — die Karten müssen also neu lesen, sonst zeigen sie weiter den
          // Zustand des alten Namens. `PATCH /api/customers/:id` stößt bei echter Firmenänderung
          // im Hintergrund selbst einen Abruf an (routes/customers.ts, M-43).
          hrQ.reload();
          placesQ.reload();
        }}
      />

      {/* Dedup-Plan Phase 2: die Zusammenfuehrung — Suche, Gegenueberstellung, Richtung,
          Bestaetigung. Serverseitig: POST /api/customers/:id/merge (:id = Ziel). */}
      <KundeZusammenfuehren
        offen={mergeOffen}
        aktueller={customer}
        alleEvents={eventsQ.data ?? []}
        vorauswahlId={mergeVorauswahlId}
        onClose={() => {
          setMergeOffen(false);
          // ?merge= raeumen, sonst oeffnete ein Reload den Drawer erneut.
          if (mergeVorauswahlId) setSearchParams({}, { replace: true });
        }}
        onMerged={(zielId) => {
          setMergeOffen(false);
          if (mergeVorauswahlId) setSearchParams({}, { replace: true });
          if (zielId === customer.id) {
            // Das Ziel ist die offene Seite: alles neu lesen, die Vorgaenge der Quelle sind jetzt hier.
            customerQ.reload();
            eventsQ.reload();
            notesQ.reload();
            placesQ.reload();
          } else {
            navigate(`/kunden/${zielId}`);
          }
        }}
      />
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * Dedup-Plan Phase 2 (Antoine 30.08.2026) — Kunde zusammenfuehren
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Der Drawer zur Kunden-Zusammenfuehrung.
 *
 * Drei Schritte in einer Flaeche: (1) den ANDEREN Kunden suchen (dieselbe Kundenliste wie
 * /kunden, gefiltert um Grabsteine/anonymisierte Datensaetze und den offenen Kunden selbst),
 * (2) GEGENUEBERSTELLUNG beider Datensaetze mit klarer Richtungsanzeige — die Richtung ist
 * waehlbar (Tausch-Knopf), Standard: der gerade offene Kunde bleibt —, (3) explizite
 * Bestaetigung per Checkbox, erst dann ist der Knopf scharf.
 *
 * Warum die Gegenueberstellung Pflicht ist und der KI-Assistent hier bewusst KEIN Werkzeug hat:
 * die Zusammenlegung ist irreversibel (kein Entmerge), und "das sind dieselben Menschen" ist
 * eine Entscheidung, die beide Datensaetze vor Augen braucht (apps/api/test/agent-ausnahmen.ts).
 */
function KundeZusammenfuehren({
  offen,
  aktueller,
  alleEvents,
  vorauswahlId = null,
  onClose,
  onMerged,
}: {
  offen: boolean;
  aktueller: Customer;
  /** ALLE Events des Mandanten — fuer die Vorgangszahl je Kandidat in der Gegenueberstellung. */
  alleEvents: EventRow[];
  /** Dedup-Plan Phase 4: der andere Kunde eines Kandidatenpaars (?merge=… von der Kundenliste). */
  vorauswahlId?: string | null;
  onClose: () => void;
  onMerged: (zielId: string) => void;
}) {
  const kundenQ = useApi<Customer[]>(offen ? "/api/customers" : null);
  const [suche, setSuche] = useState("");
  const [andererId, setAndererId] = useState<string | null>(null);
  /** Wer ueberlebt? Standard: der gerade offene Kunde. */
  const [aktuellerBleibt, setAktuellerBleibt] = useState(true);
  const [bestaetigt, setBestaetigt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Beim (erneuten) Oeffnen sauber starten — ein liegen gebliebener Haken aus dem letzten
  // Versuch darf keine neue Zusammenfuehrung vorbestaetigen.
  useEffect(() => {
    if (offen) {
      setSuche("");
      // Dedup-Plan Phase 4: kommt der Drawer vom Dubletten-Review (?merge=…), steht der andere
      // Kunde schon fest — die Gegenueberstellung oeffnet direkt. Bestaetigung bleibt IMMER leer.
      setAndererId(vorauswahlId ?? null);
      setAktuellerBleibt(true);
      setBestaetigt(false);
      setFehler(null);
    }
  }, [offen, vorauswahlId]);

  const vorgaengeJeKunde = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of alleEvents) map.set(e.customerId, (map.get(e.customerId) ?? 0) + 1);
    return map;
  }, [alleEvents]);

  const kandidaten = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return (kundenQ.data ?? [])
      .filter((k) => k.id !== aktueller.id && !k.mergedIntoId && !k.anonymizedAt)
      // Bug 02.09.2026 (weisser Bildschirm beim Tippen): k.email lief ungenullcheckt, obwohl
      // E-Mail seit sql/129 optional ist (Schwesterproblem zu customers.tsx, Commit 433fd513).
      // name ist laut DB zwar NOT NULL, wird hier aber aus demselben Grund defensiv abgesichert
      // wie email/company — der TS-Typ hat schon einmal gegenueber der Laufzeit gelogen, und
      // KEIN Tastendruck darf die ganze Seite (kein Error Boundary vorhanden) mitreissen.
      .filter(
        (k) =>
          !q ||
          (k.name ?? "").toLowerCase().includes(q) ||
          (k.email ?? "").toLowerCase().includes(q) ||
          (k.company ?? "").toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [kundenQ.data, suche, aktueller.id]);

  const anderer = useMemo(() => (kundenQ.data ?? []).find((k) => k.id === andererId) ?? null, [kundenQ.data, andererId]);
  const ziel = anderer ? (aktuellerBleibt ? aktueller : anderer) : null;
  const quelle = anderer ? (aktuellerBleibt ? anderer : aktueller) : null;

  async function zusammenfuehren() {
    if (!ziel || !quelle || !bestaetigt) return;
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/customers/${ziel.id}/merge`, "POST", { sourceCustomerId: quelle.id, confirm: true });
      onMerged(ziel.id);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const zeile = (label: string, wert: (k: Customer) => string) =>
    ziel && quelle ? (
      <tr key={label} className="border-b border-outline-variant/30 last:border-0 dark:border-outline/30">
        <td className="py-2 pr-3 font-body-sm text-[11.5px] font-semibold text-secondary dark:text-secondary-fixed-dim">{label}</td>
        <td className="py-2 pr-3 font-body-sm text-[12.5px] text-on-background dark:text-inverse-on-surface">{wert(ziel) || "—"}</td>
        <td className="py-2 font-body-sm text-[12.5px] text-on-surface-variant line-through decoration-outline-variant dark:text-secondary-fixed-dim">
          {wert(quelle) || "—"}
        </td>
      </tr>
    ) : null;

  return (
    <Drawer open={offen} onClose={onClose} title="Kunden zusammenführen">
      <div className="space-y-4">
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Zwei Datensätze derselben Person/Firma werden zu einem. Der aufgelöste Kunde bleibt als Verweis erhalten, alle Vorgänge, Anfragen
          und Notizen wandern zum verbleibenden Kunden. <strong>Dieser Schritt kann nicht rückgängig gemacht werden.</strong>
        </p>

        {/* Schritt 1: den anderen Kunden finden */}
        <label className="block">
          <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
            Anderen Kunden suchen
          </span>
          <Input
            placeholder="Name, Firma oder E-Mail …"
            value={suche}
            onChange={(e) => {
              setSuche(e.target.value);
              setAndererId(null);
              setBestaetigt(false);
            }}
          />
        </label>
        {kundenQ.error && <ErrorNote message={kundenQ.error} onRetry={kundenQ.reload} />}
        {!anderer && (
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {kundenQ.loading ? (
              <LoadingRows rows={3} />
            ) : kandidaten.length === 0 ? (
              <p className="py-2 text-[12.5px] text-secondary-fixed-dim">Keine passenden Kunden gefunden.</p>
            ) : (
              kandidaten.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  className="flex w-full items-center gap-3 rounded-lg border border-outline-variant/40 px-3 py-2 text-left transition-colors hover:border-primary/50 hover:bg-surface-container-high/20 dark:border-outline/30"
                  onClick={() => {
                    setAndererId(k.id);
                    setBestaetigt(false);
                  }}
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/14 font-label-md text-[11px] font-bold text-primary">
                    {initials(k.name)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-body-sm text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                      {k.name}
                      {k.company ? ` · ${k.company}` : ""}
                    </span>
                    <span className="block truncate text-[11px] text-secondary-fixed-dim">
                      {k.email} · {vorgaengeJeKunde.get(k.id) ?? 0} Vorgänge
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        )}

        {/* Schritt 2: Gegenueberstellung + Richtung */}
        {anderer && ziel && quelle && (
          <>
            <div className="rounded-xl border border-outline-variant/50 p-3.5 dark:border-outline/30">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="font-body-sm text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                  „{quelle.name}“ wird in „{ziel.name}“ aufgelöst
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  className="!py-1.5"
                  title="Richtung tauschen — der andere Kunde bleibt"
                  onClick={() => {
                    setAktuellerBleibt((v) => !v);
                    setBestaetigt(false);
                  }}
                >
                  <Icon name="swap_horiz" className="text-[16px]" /> Richtung tauschen
                </Button>
              </div>
              <p className="mb-3 text-[11.5px] leading-[1.5] text-secondary dark:text-secondary-fixed-dim">
                „{ziel.name}“ behält seine Daten; leere Felder werden aus „{quelle.name}“ ergänzt. Alle Vorgänge, Anfragen und Notizen
                wandern zu „{ziel.name}“.
              </p>
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-outline-variant/40 dark:border-outline/30">
                    <th className="py-1.5 pr-3 font-label-md text-[10.5px] font-semibold uppercase tracking-wide text-secondary-fixed-dim" />
                    <th className="py-1.5 pr-3 font-label-md text-[10.5px] font-semibold uppercase tracking-wide text-primary">Bleibt</th>
                    <th className="py-1.5 font-label-md text-[10.5px] font-semibold uppercase tracking-wide text-secondary-fixed-dim">
                      Wird aufgelöst
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {zeile("Name", (k) => k.name)}
                  {zeile("E-Mail", (k) => k.email)}
                  {zeile("Telefon", (k) => k.phone ?? "")}
                  {zeile("Firma", (k) => k.company ?? "")}
                  {zeile("Vorgänge", (k) => String(vorgaengeJeKunde.get(k.id) ?? 0))}
                </tbody>
              </table>
              <button
                type="button"
                className="mt-2 text-[11.5px] font-semibold text-secondary underline-offset-2 hover:underline dark:text-secondary-fixed-dim"
                onClick={() => {
                  setAndererId(null);
                  setBestaetigt(false);
                }}
              >
                Anderen Kunden wählen
              </button>
            </div>

            {/* Schritt 3: explizite Bestaetigung */}
            <label className="flex cursor-pointer items-start gap-2.5">
              <Checkbox
                className="mt-0.5"
                checked={bestaetigt}
                onChange={(e) => setBestaetigt(e.target.checked)}
              />
              <span className="font-body-sm text-[12.5px] text-on-background dark:text-inverse-on-surface">
                Ich habe beide Datensätze geprüft: es ist dieselbe Kundin/derselbe Kunde. „{quelle.name}“ soll unwiderruflich in „
                {ziel.name}“ aufgelöst werden.
              </span>
            </label>
          </>
        )}

        {fehler && <ErrorNote message={fehler} />}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="button" disabled={!ziel || !quelle || !bestaetigt || busy} onClick={() => void zusammenfuehren()}>
            {busy ? "Führt zusammen …" : "Jetzt zusammenführen"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * Migration 245 — Kunde bearbeiten
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Das Formular zum Ändern eines BESTEHENDEN Kunden.
 *
 * ── Warum es das Fundament der drei anderen Punkte ist ───────────────────────────────────────
 * `PATCH /api/customers/:id` gibt es seit Langem; was fehlte, war die Fläche dazu — `apps/web`
 * kannte nur „Kunde anlegen" (`pages/customers.tsx`). Deshalb hatte die Handlung „Anderen Namen
 * versuchen" aus § 4.2 kein Ziel, und `@maestro/anreicherung` gab bei `kein_treffer` bewusst
 * `null` zurück („keine Schaltfläche ohne Wirkung"). Ohne dieses Formular bleibt jede Korrektur
 * einer falschen Zuordnung ein halber Weg: man kann sagen, dass es falsch ist, aber nichts
 * richtigstellen.
 *
 * ── Felder ────────────────────────────────────────────────────────────────────────────────────
 * Name, E-Mail, Firma, Telefon, Steuernummer und — seit Punkt 113 (Antoine 24.09.2026, „Ja,
 * Adresse beim Kunden") — die Kundenadresse samt Land. Sie schreibt in dieselben Spalten, in die
 * auch die Places-Karte feldweise übernimmt (`customers.address_*`); es bleibt EINE Anschrift. Die
 * Auftragsadresse eines Vorgangs ist davon getrennt und wird am Vorgang bearbeitet. Anlass: ohne
 * Land lehnt LexOffice jede Rechnung ab, und es gab keine Stelle, es beim Kunden einzutragen.
 *
 * ── Es wird nur geschickt, was sich geändert hat ─────────────────────────────────────────────
 * Ein PATCH mit allen vier Feldern würde bei jedem Speichern die Firma neu setzen — und
 * `routes/customers.ts` stößt bei einer FIRMENÄNDERUNG einen Registerabruf an (M-43). Der
 * Vergleich mit dem Ausgangsstand ist damit kein Feinschliff, sondern der Schutz des
 * 50/h-Deckels: „Telefonnummer korrigiert" darf keinen externen Abruf kosten.
 */
function KundeBearbeiten({
  offen,
  kunde,
  fokus,
  onClose,
  onGespeichert,
}: {
  offen: boolean;
  kunde: Customer | null;
  fokus: "name" | "company";
  onClose: () => void;
  onGespeichert: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [firma, setFirma] = useState("");
  const [telefon, setTelefon] = useState("");
  const [steuernummer, setSteuernummer] = useState("");
  const [adresse, setAdresse] = useState<AdresseWerte>({ street: null, addition: null, zip: null, city: null, countryCode: null });
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Beim Öffnen den aktuellen Stand übernehmen — nicht beim Mounten: die Karte bleibt gemountet,
  // und ein zwischenzeitlich (z.B. aus der Places-Karte) übernommener Wert stünde sonst nicht drin.
  useEffect(() => {
    if (!offen || !kunde) return;
    setName(kunde.name);
    setEmail(kunde.email);
    setFirma(kunde.company ?? "");
    setTelefon(kunde.phone ?? "");
    setSteuernummer(kunde.taxNumber ?? "");
    setAdresse({
      street: kunde.addressStreet,
      addition: kunde.addressAddition,
      zip: kunde.addressZip,
      city: kunde.addressCity,
      countryCode: kunde.addressCountryCode,
    });
    setFehler(null);
  }, [offen, kunde]);

  if (!kunde) return null;

  async function speichern(e: FormEvent) {
    e.preventDefault();
    if (!kunde) return;
    setBusy(true);
    setFehler(null);
    // Leer getippt heisst „keine Angabe" = null, nicht der leere String: `company`/`phone` sind
    // nullbar, und ein leerer String liefe als „Firma vorhanden, Name ist nichts" durch jede
    // spätere Prüfung.
    const alsWert = (s: string): string | null => (s.trim() ? s.trim() : null);
    const patch: Record<string, string | null> = {};
    if (name.trim() && name.trim() !== kunde.name) patch.name = name.trim();
    if (email.trim() && email.trim() !== kunde.email) patch.email = email.trim();
    if (alsWert(firma) !== (kunde.company ?? null)) patch.company = alsWert(firma);
    if (alsWert(telefon) !== (kunde.phone ?? null)) patch.phone = alsWert(telefon);
    if (alsWert(steuernummer) !== (kunde.taxNumber ?? null)) patch.taxNumber = alsWert(steuernummer);
    const adressFelder = [
      ["addressStreet", adresse.street],
      ["addressAddition", adresse.addition],
      ["addressZip", adresse.zip],
      ["addressCity", adresse.city],
    ] as const;
    for (const [feld, wert] of adressFelder) {
      if (alsWert(wert ?? "") !== (kunde[feld] ?? null)) patch[feld] = alsWert(wert ?? "");
    }
    // Das Land nur, wenn eines GEWAEHLT wurde: der Radix-Select meldet in einem Formular beim
    // Einhaengen gelegentlich "" (verstecktes natives Auswahlfeld) — das darf kein Land loeschen.
    if (adresse.countryCode && adresse.countryCode !== kunde.addressCountryCode) patch.addressCountryCode = adresse.countryCode;
    if (Object.keys(patch).length === 0) {
      // Nichts geändert ist kein Fehler — und auch kein Grund, einen Request zu schicken.
      setBusy(false);
      onClose();
      return;
    }
    try {
      await apiMutate(`/api/customers/${kunde.id}`, "PATCH", patch);
      onGespeichert();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer open={offen} onClose={onClose} title="Kunde bearbeiten">
      <form className="space-y-4" onSubmit={(e) => void speichern(e)}>
        {fehler && <ErrorNote message={fehler} />}
        <label className="block">
          <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
            Name *
          </span>
          <Input
            required
            value={name}
            autoFocus={fokus === "name"}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
            E-Mail *
          </span>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
            Firma
          </span>
          <Input
            value={firma}
            autoFocus={fokus === "company"}
            onChange={(e) => setFirma(e.target.value)}
          />
          {/* Beschreibend, nicht wertend: was der Betrieb wissen muss, bevor er tippt — nicht,
              was er tun soll. Der Satz steht hier, weil genau dieses Feld die Recherche neu
              anstößt und der Abruf ein knappes, geteiltes Kontingent verbraucht. */}
          <span className="mt-1 block font-body-sm text-[11px] text-on-surface-variant/70">
            Ein anderer Firmenname wird neu im Handelsregister nachgeschlagen.
          </span>
        </label>
        <label className="block">
          <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
            Telefon
          </span>
          <Input value={telefon} onChange={(e) => setTelefon(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
            Steuernummer
          </span>
          <Input value={steuernummer} onChange={(e) => setSteuernummer(e.target.value)} />
          {/* Finanzamt-Steuernummer, bewusst getrennt von der USt-IdNr. (Firmenprofil-Karte
              weiter unten) — beides sind unterschiedliche Kennungen. */}
          <span className="mt-1 block font-body-sm text-[11px] text-on-surface-variant/70">
            Finanzamt-Steuernummer, nicht die USt-IdNr.
          </span>
        </label>
        {/* Punkt 113: die Kundenadresse (Rechnungsanschrift, sofern der Vorgang keine eigene
            Auftragsadresse hat). */}
        <AdresseFelder
          werte={adresse}
          onFeldChange={(feld, wert) => setAdresse((a) => ({ ...a, [feld]: wert }))}
          laender={LAENDER}
          testIdPrefix="kunde-bearbeiten"
        />
        {kunde.addressCountryAbgeleitetAm && (!adresse.countryCode || adresse.countryCode === kunde.addressCountryCode) && (
          <p data-testid="kunde-bearbeiten-land-abgeleitet" className="-mt-2 font-body-sm text-[11px] text-on-surface-variant/70">
            Land aus PLZ und Ort abgeleitet am {formatDate(kunde.addressCountryAbgeleitetAm)} — bitte prüfen.
          </p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? "Speichert …" : "Speichern"}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

/** Basis-Suchseite fuer die manuelle Insolvenz-Pruefung — bewusst OHNE Query-Parameter (kein
 *  Auto-Fill, kein automatisierter Abruf). Der Name muss selbst eingegeben werden, siehe
 *  Welle-B-Plan ("robots.txt verbietet Automatisierung, DSGVO-sensible personenbezogene Daten"). */
const INSOLVENZ_SEARCH_URL = "https://neu.insolvenzbekanntmachungen.de/";

/** Anzeigename der Modul-Kachel — muss zu `settings.tsx` passen, sonst schickt der Satz
 *  „Modul ‚…' ist ausgeschaltet." den Betreiber zu einer Kachel, die er dort nicht findet. */
const HR_MODUL_NAME = "Handelsregister-Check";
/** Ziel des „Sprung in die Kachel" aus Konzept § 4.2 (settings.tsx liest ?tab= aus der URL). */
const MODUL_KACHEL_URL = "/einstellungen?tab=module";

/**
 * Die Herkunftszeile unter einer Recherche-Angabe: „Handelsregister · Stand 12.06.2026" bzw.
 * „Handelsregister · ohne Datum" (R1.6, Konzept § 2.2). Den Text baut ausschliesslich
 * `standZeile()` aus `@maestro/recherche-stand` — hier steht nur, wie er aussieht. Wer ihn hier
 * zusammensetzte, koennte unbemerkt auf `createdAt` zurueckfallen: `apps/web` hat keinen
 * Test-Runner, und ein falsches Datum sieht aus wie ein richtiges.
 */
function Herkunft({ zeile }: { zeile: string }) {
  return <p className="mt-1 font-body-sm text-[11px] text-on-surface-variant/60">{zeile}</p>;
}

/**
 * Karte „Firmenprofil" im Kunden-Profil: Handelsregister + Bundesanzeiger — TATSACHEN, keine
 * Bewertung (R1.7, Entscheidung P-4, Konzept § 6.1).
 *
 * ── Was sich am 08.08.2026 geaendert hat ─────────────────────────────────────────────────────
 * Die Karte hiess frueher anders und begann mit 2-4 Saetzen KI-formulierter Bonitaetsprosa
 * (`ai_summary`). Beides ist weg: der Name machte eine Erwartung, die MAESTRO nicht einloest, und
 * die Prosa war eine erzeugte Einschaetzung ueber (auch) natuerliche Personen. § 31 BDSG verlangt
 * fuer einen Wahrscheinlichkeitswert ein anerkanntes mathematisch-statistisches Verfahren; ein
 * Sprachmodell ist keins. GATE-B (juristische Abnahme eines umformulierten Prompts) ist am
 * 07.08.2026 entfallen — Antoine kauft stattdessen einen Dienst zu. Damit gilt die Rueckfallebene
 * aus docs/GATE-B-JURISTISCHE-PRUEFUNG.md § 6: ersatzlos streichen, Registerfakten und eigene
 * Vertragserfahrung bleiben.
 *
 * Was hier steht, ist deshalb ausnahmslos beschreibend: Feld, Wert, Quelle, Stand. Keine Ampel,
 * keine Note, keine Prognose, kein Schwellenwert. Ein GEKAUFTER Score bekommt seinen Platz ueber
 * `gekaufterScoreAngabe()` (@maestro/firmenprofil) — angezeigt, nie erzeugt, nie ausloesend.
 * Die Wortlaut-Ratsche dazu: apps/api/test/firmenprofil-wortlaut.test.ts.
 *
 * R0.7: Diese Komponente RENDERT nur noch. Welcher Zustand vorliegt, welcher Satz dazu gehört und
 * ob es eine Handlung gibt, entscheidet `anreicherungsAnzeige()` aus `@maestro/anreicherung` —
 * eine reine Funktion, die dort liegt, wo Tests laufen (`apps/web` hat keinen Test-Runner). Der
 * frühere Einheitssatz (ein „… für <Firma>" mit dem Zusatz, der Abruf laufe im Hintergrund)
 * behauptete bei jeder Datenlage dasselbe. Auf Produktion war das für alle 27 Firmen falsch — es
 * lief nie ein Abruf. Die Ratsche dagegen: apps/api/test/anreicherung-zustand.test.ts (B).
 */
/**
 * R3.2 — die USt-IdNr.-Bestätigung im Kunden-Profil (Datenanreicherung, § 18e UStG).
 *
 * Diese Komponente ENTSCHEIDET NICHTS. Welcher Satz gilt, ob es ein Ergebnis gibt und wie es
 * formuliert wird, sagt `ustIdKarte()` aus `@maestro/ustid-anzeige` — dort ist die Regel ohne DOM
 * geprüft (`apps/api/test/ustid.test.ts`), und der gerenderte Baum wird zusätzlich in
 * `apps/web/test/ustid-karte.test.tsx` geprüft.
 *
 * Der Punkt, um den es geht: Was hier steht, ist eine **Momentaufnahme**. Es gibt in dieser Karte
 * keine Stelle, an der „gültig“ ohne Zeitpunkt erscheinen kann — der Ergebnissatz kommt fertig
 * datiert aus der Regel, und der Hinweis auf die Reichweite steht immer daneben.
 */
function UstIdCard({
  customerId,
  company,
  antwort,
  loading,
  onReload,
}: {
  customerId: string;
  company: string;
  antwort: UstIdAntwort | null;
  loading: boolean;
  onReload: () => void;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [nummer, setNummer] = useState("");

  const karte = ustIdKarte({
    modulAktiv: antwort?.modulAktiv ?? false,
    firma: company,
    ustIdNr: antwort?.ustIdNr ?? null,
    // „Nicht eingerichtet" hängt an der eigenen Nummer des Betriebs. Die Route liefert nur, OB es
    // sie gibt — das Stammdatum selbst gehört nicht in eine Kunden-Antwort.
    eigeneUstIdNr: antwort?.eigeneVorhanden ? "vorhanden" : null,
    zeile: (antwort?.zeile ?? null) as UstIdZeile | null,
    jetzt: new Date(),
  });

  async function bestaetigen() {
    setLaeuft(true);
    setHinweis(null);
    const res = await neuabrufAnstossen({ kundeId: customerId }, "/api/anreicherung/ustid");
    setHinweis(res.hinweis);
    setLaeuft(false);
    if (res.ok) onReload();
  }

  async function nummerSpeichern() {
    const wert = nummer.trim();
    if (!wert) return;
    setLaeuft(true);
    setHinweis(null);
    try {
      await apiMutate(`/api/customers/${customerId}`, "PATCH", { vatId: wert });
      setNummer("");
      onReload();
    } catch (err) {
      setHinweis(err instanceof Error ? err.message : String(err));
    } finally {
      setLaeuft(false);
    }
  }

  const fehltNummer = (antwort?.modulAktiv ?? false) && (antwort?.eigeneVorhanden ?? false) && !antwort?.ustIdNr;

  return (
    <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low p-5 dark:border-outline/30 dark:bg-surface-variant/10">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
          USt-IdNr.-Bestätigung
        </p>
        <button
          type="button"
          onClick={onReload}
          className="font-label-md text-label-md text-primary hover:underline dark:text-primary-fixed-dim"
        >
          Aktualisieren
        </button>
      </div>

      {loading ? (
        <LoadingRows rows={2} />
      ) : (
        <div className="space-y-3">
          {karte.satz && (
            <p
              className={
                karte.zustand === "fehler"
                  ? "font-body-sm text-body-sm text-error"
                  : "font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim"
              }
            >
              {karte.satz}
            </p>
          )}

          {/* Die Kernaussage — immer mit ihrem Zeitpunkt, nie „gültig“ allein. */}
          {karte.ergebnis && (
            <div className="space-y-2">
              <p className="font-body-md text-body-md text-on-background dark:text-inverse-on-surface">{karte.ergebnis}</p>
              {karte.momentaufnahme && (
                <p className="font-body-sm text-[11.5px] italic text-secondary dark:text-secondary-fixed-dim">
                  {karte.momentaufnahme}
                </p>
              )}
              {karte.zeilen.length > 0 && (
                <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                  {karte.zeilen.map((z) => (
                    <div key={z.feld} className="flex flex-wrap items-baseline gap-x-2">
                      <dt className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">{z.feld}:</dt>
                      <dd className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">{z.wert}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {karte.herkunft && (
                <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">{karte.herkunft}</p>
              )}
            </div>
          )}

          {/* Die Fläche für die Eingabe steht dort, wo das Ergebnis erscheint — kein Verweis auf
              einen anderen Menüpunkt (Antoines Vorgabe seit PR #411). */}
          {fehltNummer && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="min-w-[12rem] flex-1">
                <span className="mb-1 block font-body-sm text-[11px] font-semibold uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                  USt-IdNr. dieses Kunden
                </span>
                <Input value={nummer} placeholder="ATU12345678" onChange={(e) => setNummer(e.target.value)} />
              </label>
              <Button type="button" variant="secondary" disabled={laeuft || !nummer.trim()} onClick={() => void nummerSpeichern()}>
                Speichern
              </Button>
            </div>
          )}

          {karte.handlung?.art === "neu_abrufen" && (
            <Button type="button" variant="secondary" disabled={laeuft} onClick={() => void bestaetigen()}>
              {laeuft ? "Läuft …" : karte.handlung.beschriftung}
            </Button>
          )}
          {hinweis && <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{hinweis}</p>}
        </div>
      )}
    </div>
  );
}

function HandelsregisterCard({
  customerId,
  company,
  modulAktiv,
  lookup,
  loading,
  onReload,
  onFirmaBearbeiten,
}: {
  customerId: string;
  company: string;
  modulAktiv: boolean;
  lookup: HandelsregisterLookup | null;
  loading: boolean;
  onReload: () => void;
  /** Migration 245: „Anderen Namen versuchen" öffnet das Kunden-Formular auf dem Firmenfeld. */
  onFirmaBearbeiten: () => void;
}) {
  // R0.13: der manuelle Neuabruf. `laeuft` sagt nur, dass der Klick unterwegs ist — den Zustand
  // danach liest die Karte aus der Route (`onReload`), nicht aus dieser Variable.
  const [laeuft, setLaeuft] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);
  /** Migration 245: welcher Treffer gerade gewählt wird (`null` = keiner), bzw. ob gerade
   *  verworfen wird. Reiner Klick-Zustand — was danach gilt, liest die Karte aus der Route. */
  const [waehlend, setWaehlend] = useState<string | null>(null);
  const [verwerfend, setVerwerfend] = useState(false);
  const matches = lookup?.matches ?? [];
  const primary = matches[0] ?? null;
  const filings = lookup?.bundesanzeigerFilings?.filings ?? [];
  const searchUrl = lookup?.bundesanzeigerFilings?.searchUrl ?? null;

  // Fuer die manuelle Insolvenz-Pruefung: Firma + jeder gefundene Geschaeftsfuehrer-/Prokura-Name
  // (dedupliziert, ueber alle Treffer hinweg falls mehrdeutig) — reine Verlinkung, siehe unten.
  const insolvencyNames = Array.from(
    new Set([primary?.companyName ?? company, ...matches.flatMap((m) => m.representatives)].filter(Boolean)),
  );

  // Die EINE Stelle, an der aus der Datenlage ein Zustand wird (§ 4.2). `jetzt` und die Zeitzone
  // kommen aus der Laufzeit des Betrachters — deshalb steht der unreine Teil hier und nicht in der
  // Funktion: "nächster Versuch gegen HH:MM" muss in seiner Zone stehen, nicht in UTC.
  const anzeige = anreicherungsAnzeige({
    modulAktiv,
    modulName: HR_MODUL_NAME,
    firma: company,
    jetzt: new Date(),
    zeile: lookup
      ? {
          status: lookup.status,
          errorCode: lookup.errorCode,
          errorMessage: lookup.errorMessage,
          lookedUpAt: lookup.lookedUpAt,
          validUntil: lookup.validUntil,
          nextRetryAt: lookup.nextRetryAt,
          createdAt: lookup.createdAt,
          trefferAnzahl: matches.length,
          verworfenAm: lookup.verworfenAm,
        }
      : null,
  });
  // Der gespeicherte Stand bleibt sichtbar, auch wenn er veraltet ist (§ 4.1: "Abgelaufen heißt
  // nicht gelöscht") und auch bei Mehrdeutigkeit (die Treffer SIND die Information).
  const zeigtDaten = anzeige.zustand === "aktuell" || anzeige.zustand === "veraltet" || anzeige.zustand === "mehrdeutig";

  /* ── Migration 245: die Trefferauswahl und das Verwerfen ──────────────────────────────────── */

  // Ob eine Auswahl offensteht, entscheidet die reine Regel — nicht dieses JSX. Sie öffnet nur
  // bei 'ambiguous' UND mindestens zwei Treffern (ein 'ambiguous' mit einem Treffer wäre ein
  // Widerspruch in den Daten, und ein Knopf darüber würde ihn verstecken).
  const auswahl: Trefferliste | null = registerTrefferauswahl({
    status: lookup?.status,
    treffer: matches,
    lookedUpAt: lookup?.lookedUpAt,
  });
  // Die Version, auf die sich jede Entscheidung bezieht (M-45). Ohne `lookedUpAt` gibt es keine
  // bestätigbare Version — dann wird gar nichts angeboten, statt blind zu schreiben.
  const version = lookup?.id && lookup.lookedUpAt ? { lookupId: lookup.id, lookedUpAt: lookup.lookedUpAt } : null;
  // Verworfen werden kann nur ein ERGEBNIS (dieselbe Grenze wie in sql/245).
  const kannVerwerfen = version !== null && (lookup?.status === "found" || lookup?.status === "ambiguous");

  async function trefferWaehlen(index: number) {
    if (!version) return;
    setWaehlend(String(index));
    setHinweis(null);
    const { hinweis: h } = await registerTrefferWaehlen({ kundeId: customerId }, version, index);
    setHinweis(h);
    setWaehlend(null);
    onReload();
  }

  async function ergebnisVerwerfen() {
    if (!version) return;
    setVerwerfend(true);
    setHinweis(null);
    const { hinweis: h } = await registerErgebnisVerwerfen({ kundeId: customerId }, version);
    setHinweis(h);
    setVerwerfend(false);
    onReload();
  }

  return (
    <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low p-5 dark:border-outline/30 dark:bg-surface-variant/10">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
          Firmenprofil
        </p>
        <button
          type="button"
          onClick={onReload}
          className="font-label-md text-label-md text-primary hover:underline dark:text-primary-fixed-dim"
        >
          Aktualisieren
        </button>
      </div>

      {loading ? (
        <LoadingRows rows={2} />
      ) : (
        <div className="space-y-4">
          {/* § 4.2: EIN Satz je Zustand, daneben höchstens EINE Handlung. */}
          {anzeige.satz && (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <p
                className={
                  anzeige.zustand === "fehler"
                    ? "font-body-sm text-body-sm text-error"
                    : "font-body-sm text-body-sm text-on-surface-variant/70"
                }
              >
                {anzeige.satz}
              </p>
              {anzeige.handlung?.art === "modul_kachel" && (
                <Link
                  to={MODUL_KACHEL_URL}
                  className="font-label-md text-label-md text-primary hover:underline dark:text-primary-fixed-dim"
                >
                  {anzeige.handlung.beschriftung}
                </Link>
              )}
              {/* R0.13 (§ 4.3, Mechanismus 4): „Jetzt abrufen" / „Erneut versuchen" / „Neu
                  abrufen" — welcher Text, entscheidet die reine Funktion. Der Abruf läuft durch
                  denselben Stundendeckel wie der automatische Weg; wird er abgewiesen, steht der
                  Grund darunter statt eines roten Fehlers. */}
              {anzeige.handlung?.art === "neu_abrufen" && (
                <button
                  type="button"
                  disabled={laeuft}
                  onClick={async () => {
                    setLaeuft(true);
                    setHinweis(null);
                    const { hinweis: h } = await neuabrufAnstossen({ kundeId: customerId });
                    setHinweis(h);
                    setLaeuft(false);
                    onReload();
                  }}
                  className="font-label-md text-label-md text-primary hover:underline disabled:opacity-50 dark:text-primary-fixed-dim"
                >
                  {laeuft ? "Wird angestoßen …" : anzeige.handlung.beschriftung}
                </button>
              )}
              {/* Migration 245: „Anderen Namen versuchen" (§ 4.2, Zustände `kein_treffer` und
                  `verworfen`). Bis hierher gab die reine Funktion an dieser Stelle `null` zurück,
                  weil es kein Formular zum Ändern eines bestehenden Kunden gab. */}
              {anzeige.handlung?.art === "firma_bearbeiten" && (
                <button
                  type="button"
                  onClick={onFirmaBearbeiten}
                  className="font-label-md text-label-md text-primary hover:underline dark:text-primary-fixed-dim"
                >
                  {anzeige.handlung.beschriftung}
                </button>
              )}
            </div>
          )}

          {hinweis && <p className="font-body-sm text-body-sm text-on-surface-variant/70">{hinweis}</p>}

          {/* ── Migration 245: die Trefferauswahl ──────────────────────────────────────────────
              Bis hierher trug der Zustand „mehrdeutig" im Register eine ZAHL und keine einzige
              Handlung: der Betrieb sah, dass es mehrere gibt, und konnte nichts tun. Gebaut im
              Stil der Google-Auswahl (R3.3) — dieselbe Ausgabeform, derselbe Satzbau, dieselbe
              Strenge; die Regel dahinter ist `registerTrefferauswahl()`. */}
          {auswahl && version && (
            <div className="space-y-3">
              <p className="font-body-sm text-body-sm text-on-surface-variant">{auswahl.satz}</p>
              <Herkunft zeile={auswahl.herkunft} />
              <ul className="space-y-2">
                {auswahl.eintraege.map((eintrag) => (
                  <li
                    key={eintrag.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-outline-variant/30 p-3 dark:border-outline/20"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-label-md text-label-md text-on-background dark:text-inverse-on-surface">
                        {eintrag.titel}
                      </p>
                      {eintrag.zeilen.map((zeile) => (
                        <p key={zeile} className="truncate font-body-sm text-body-sm text-on-surface-variant/80">
                          {zeile}
                        </p>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      className="shrink-0 !px-3 !py-1.5"
                      disabled={waehlend !== null || verwerfend}
                      onClick={() => void trefferWaehlen(Number(eintrag.id))}
                    >
                      {waehlend === eintrag.id ? "…" : "Das ist er"}
                    </Button>
                  </li>
                ))}
              </ul>
              {/* Der Ausweg, den es vorher nicht gab. Er löscht nichts still weg, sondern hält
                  fest, dass keiner passt — sonst fragt der nächste Anlass dieselbe Firma erneut
                  gegen dasselbe Stundenkontingent ab (Begründung im Kopf von sql/245). */}
              <button
                type="button"
                disabled={waehlend !== null || verwerfend}
                onClick={() => void ergebnisVerwerfen()}
                className="font-label-md text-label-md text-primary hover:underline disabled:opacity-50 dark:text-primary-fixed-dim"
              >
                {verwerfend ? "Wird vermerkt …" : "Keiner davon"}
              </button>
            </div>
          )}

          {zeigtDaten && lookup && (
            <div className="space-y-4">
              {/* HIER stand bis R1.7 die KI-Kurzzusammenfassung (Bonitäts-Einordnung). Ersatzlos
                  gestrichen — siehe Kopf dieser Komponente. Der Platz bleibt bewusst leer und
                  bekommt keinen Ersatztext: „keine Einschätzung verfügbar" wäre das Versprechen,
                  dass es irgendwann eine gäbe. */}
              {/* Migration 245: die frühere Aufzählung „Gefunden: A, B, C." steht jetzt als
                  auswählbare Liste weiter oben — zweimal dieselben Namen wären zweimal dieselbe
                  Information, einmal ohne Handlung. Bleibt nur der Fall, in dem die Auswahl-Regel
                  NICHT öffnet (Mehrdeutigkeit mit einem einzigen Treffer, ein Widerspruch in den
                  Daten): dort sagt die Karte, was dasteht, statt zu schweigen. */}
              {anzeige.zustand === "mehrdeutig" && !auswahl && (
                <div>
                  <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                    Gefunden: {matches.map((m) => m.companyName).join(", ")}.
                  </p>
                  <Herkunft zeile={standZeile("Handelsregister", lookup.lookedUpAt)} />
                </div>
              )}

              {/* Rohdaten — nur bei eindeutigem Treffer sinnvoll darstellbar.
                  R1.7: IMMER alle fuenf Felder, auch die leeren. Welche es sind, was in einem
                  leeren steht und wie das Eintragungsdatum formatiert wird, entscheidet
                  `registerFelder()` aus @maestro/firmenprofil — nicht dieses JSX. Vorher fiel ein
                  fehlendes Feld ersatzlos weg, und man sah der Karte nicht an, ob nichts vorliegt
                  oder nichts geliefert wurde. */}
              {lookup.status === "found" && primary && (
                <div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 font-body-sm text-body-sm">
                    {registerFelder(primary).map((feld) => (
                      <Fragment key={feld.label}>
                        <dt className="text-secondary dark:text-secondary-fixed-dim">{feld.label}</dt>
                        <dd
                          className={
                            feld.wert
                              ? "text-on-background dark:text-inverse-on-surface"
                              : "text-on-surface-variant/60"
                          }
                        >
                          {feld.wert ?? OHNE_WERT}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                  {/* Alle Felder oben stammen aus EINEM Abruf — deshalb eine Herkunftszeile fuer den
                      ganzen Block statt fuenfmal derselben. „Letzte Eintragung" ist das Datum des
                      Registereintrags, NICHT der Stand der Auskunft. */}
                  <Herkunft zeile={standZeile("Handelsregister", lookup.lookedUpAt)} />
                  {/* Migration 245: auch ein EINDEUTIGER Treffer kann der falsche sein — zwei
                      Betriebe mit demselben Namen, ein Namensvetter, eine übernommene Firma.
                      Ohne diesen Weg stünde ein fremder Registereintrag dauerhaft an diesem
                      Kunden, und die einzige Handlung wäre gewesen, ihn zu ignorieren. */}
                  {kannVerwerfen && (
                    <button
                      type="button"
                      disabled={verwerfend || waehlend !== null}
                      onClick={() => void ergebnisVerwerfen()}
                      className="mt-1.5 font-label-md text-label-md text-primary hover:underline disabled:opacity-50 dark:text-primary-fixed-dim"
                    >
                      {verwerfend ? "Wird vermerkt …" : "Gehört nicht zu diesem Kunden"}
                    </button>
                  )}
                </div>
              )}

              {/* Bundesanzeiger-Veröffentlichungen (Titel + Datum, nie Einzeldokumente abgerufen). */}
              <div>
                <p className="mb-1.5 font-label-md text-[11px] uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                  Bundesanzeiger-Veröffentlichungen
                </p>
                {filings.length === 0 ? (
                  <>
                    {/* Auch ein Nein ist eine Aussage ueber die Welt und altert. Ihr Stand ist der
                        Abrufzeitpunkt — es gibt hier keine Veroeffentlichung, die ein eigenes
                        Datum mitbraechte. */}
                    <p className="font-body-sm text-body-sm text-on-surface-variant/70">Keine Veröffentlichungen gefunden.</p>
                    <Herkunft zeile={standZeile("Bundesanzeiger", lookup.lookedUpAt)} />
                  </>
                ) : (
                  <ul className="space-y-1.5">
                    {filings.map((f, i) => (
                      <li key={i} className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                        {f.title}
                        {/* Der Stand einer Veroeffentlichung ist ihr Erscheinungsdatum, nicht der
                            Abrufzeitpunkt (dieselbe Zuordnung wie in @maestro/firmenprofil, R0.9).
                            Fehlt es, steht dort „ohne Datum" — vorher fiel die Zeile ersatzlos weg
                            und der Titel sah aus wie eine Angabe von heute. */}
                        <Herkunft zeile={standZeile("Bundesanzeiger", f.publishedAt)} />
                      </li>
                    ))}
                  </ul>
                )}
                {searchUrl && (
                  <a
                    href={searchUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline dark:text-primary-fixed-dim"
                  >
                    Auf Bundesanzeiger ansehen <Icon name="open_in_new" className="text-[15px]" />
                  </a>
                )}
              </div>

              {/* Klar abgesetzt: manuelle Insolvenz-Prüfung — reine Verlinkung, KEIN automatisierter
                  Abruf/Auto-Fill (siehe Kommentar bei INSOLVENZ_SEARCH_URL). */}
              <div className="rounded-lg border-2 border-dashed border-outline-variant/50 p-3 dark:border-outline/40">
                <p className="font-label-md text-[11px] font-semibold uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                  Insolvenz-Prüfung (manuell)
                </p>
                <p className="mt-1 font-body-sm text-[11px] text-on-surface-variant/70">
                  Kein automatischer Abruf möglich (insolvenzbekanntmachungen.de verbietet Automatisierung) — Name bitte im
                  geöffneten Portal selbst eingeben.
                </p>
                <div className="mt-2 space-y-1">
                  {insolvencyNames.map((name) => (
                    <a
                      key={name}
                      href={INSOLVENZ_SEARCH_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1.5 font-body-sm text-body-sm text-primary hover:underline dark:text-primary-fixed-dim"
                    >
                      <Icon name="search" className="text-[15px]" /> {name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Anzeigename + Reihenfolge der drei uebernehmbaren Felder. rating/userRatingsTotal/
 *  openingHours/businessStatus sind bewusst NICHT hier gelistet (rein informativ, siehe
 *  ui/types.ts GooglePlacesApplicableField). */
const PLACES_FIELDS: { key: GooglePlacesApplicableField; label: string; value: (l: GooglePlacesLookup) => string | null }[] = [
  { key: "company", label: "Firmenname", value: (l) => l.suggestedName },
  { key: "phone", label: "Telefon", value: (l) => l.suggestedPhone },
  { key: "address", label: "Adresse", value: (l) => l.suggestedFormattedAddress },
];

/**
 * R3.4 + R4.4 — „Anschrift: die Quellen sagen Verschiedenes".
 *
 * Zeigt die abweichenden Anschriften NEBENEINANDER, jede mit Quelle und Stand, und darunter — wenn
 * ein LEI vorliegt — den GLEIF-Kreuzcheck. Es gibt hier bewusst KEINEN Knopf: nichts wird
 * zusammengeführt, nichts übernommen, keine Quelle zur richtigen erklärt. Die Auflösung ist eine
 * Entscheidung des Betreibers, kein Rechenschritt (Konzept § 3.4).
 *
 * Ob überhaupt etwas erscheint, entscheiden ausschließlich `adressAbgleich()` und
 * `gleifKreuzcheck()` aus `@maestro/quellenabgleich`. `apps/web` hat keinen Test-Runner —
 * Bedingungen, die hier stünden, könnte niemand prüfen (Sätze wie „übereinstimmende Angaben
 * erzeugen keinen Hinweis" oder „ohne LEI passiert nichts").
 */
function AdressenNebeneinander({
  customer,
  hrLookup,
  placesLookup,
}: {
  customer: Customer;
  hrLookup: HandelsregisterLookup | null;
  placesLookup: GooglePlacesLookup | null;
}) {
  // Die eigene Anschrift in derselben Form, in der die Fremdquellen ihre führen („Straße Nr,
  // PLZ Ort") — sonst verglichen wir Struktur gegen Text.
  const eigene = [customer.addressStreet, [customer.addressZip, customer.addressCity].filter(Boolean).join(" ")]
    .map((t) => t?.trim())
    .filter(Boolean)
    .join(", ");
  // Nur der EINDEUTIGE Registertreffer: bei „mehrere mögliche Treffer" wäre die Wahl eines davon
  // eine Erfindung (dieselbe Regel wie in @maestro/firmenprofil).
  const registerTreffer = hrLookup?.status === "found" ? (hrLookup.matches?.[0] ?? null) : null;

  const gleifLage = {
    lei: hrLookup?.lei ?? null,
    datensatz: hrLookup?.gleifRecord ?? null,
    geprueftAmIso: hrLookup?.gleifCheckedAt ?? null,
    bekannt: { firma: customer.company, adresse: eigene },
  };
  const kreuzcheck = gleifKreuzcheck(gleifLage);
  const gleifAdresse = gleifAlsAdressQuelle(gleifLage);

  const abgleich = adressAbgleich({
    eintraege: [
      { quelle: "Kundendatensatz", adresse: eigene, standIso: customer.updatedAt },
      { quelle: "Handelsregister", adresse: registerTreffer?.seat, standIso: hrLookup?.lookedUpAt },
      { quelle: "Google Places", adresse: placesLookup?.suggestedFormattedAddress, standIso: placesLookup?.lookedUpAt },
      ...(gleifAdresse ? [gleifAdresse] : []),
    ],
  });

  if (!abgleich && !kreuzcheck) return null;

  return (
    <Card>
      <CardRow title={abgleich ? GEGENUEBERSTELLUNG_TITEL : "Gegenprüfung"} />
      <div className="flex flex-col gap-3 px-[18px] py-[14px]">
        {abgleich && (
          <>
            <p className="text-[12.5px] leading-[1.55] text-on-background dark:text-inverse-on-surface">{abgleich.satz}</p>
            <div className="flex flex-col gap-2.5">
              {abgleich.eintraege.map((e) => (
                <div
                  key={`${e.quelle}-${e.adresse}`}
                  className={`rounded-lg border px-3 py-2 ${
                    e.widersprichtAnderen ? "border-secondary/40 bg-secondary/10" : "border-outline-variant/40"
                  }`}
                >
                  <p className="text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">{e.adresse}</p>
                  <p className="mt-0.5 text-[10.5px] text-secondary-fixed-dim">{e.herkunft}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] leading-[1.5] text-secondary dark:text-secondary-fixed-dim">{abgleich.hinweis}</p>
            <GoogleAttribution werte={abgleich.googleWerte} />
          </>
        )}
        {kreuzcheck && (
          <div className="border-t border-outline-variant/30 pt-3 dark:border-outline/30">
            <p className="text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">{kreuzcheck.satz}</p>
            <p className="mt-0.5 text-[10.5px] text-secondary-fixed-dim">
              {kreuzcheck.herkunft} · LEI {kreuzcheck.lei}
            </p>
            <ul className="mt-2 flex flex-col gap-1">
              {kreuzcheck.befunde.map((b) => (
                <li key={b.feld} className="text-[12px] leading-[1.5] text-on-background dark:text-inverse-on-surface">
                  <span className="font-semibold">{b.feld}:</span> {b.stimmtUeberein ? "bestätigt" : `laut GLEIF „${b.gleifWert}"`}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] leading-[1.5] text-secondary dark:text-secondary-fixed-dim">{kreuzcheck.hinweis}</p>
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Datenanreicherung Welle C: Google Places (New) auf der Kunden-Vollseite. Im Unterschied zur
 * Handelsregister-Karte (automatischer Hintergrund-Job) ist das hier ein MANUELLER, von einem
 * Menschen ausgeloester Abruf (Button "Nachschlagen") — Suchfeld vorbefuellt mit
 * "${Firma}, ${Ort}", vor dem Abruf frei editierbar. Jedes uebernehmbare Feld (Firmenname/
 * Telefon/Adresse) hat einen EIGENEN "Übernehmen"-Button, der gezielt NUR dieses eine Feld setzt
 * (nie ein Bulk-Overwrite) — nach Erfolg zeigt die Zeile "✓ übernommen" statt des Buttons.
 */
function GooglePlacesCard({
  customerId,
  company,
  addressStreet,
  addressZip,
  addressCity,
  lookup,
  loading,
  onReload,
  onCustomerUpdated,
  onFirmaBearbeiten,
}: {
  customerId: string;
  company: string | null;
  addressStreet: string | null;
  addressZip: string | null;
  addressCity: string | null;
  lookup: GooglePlacesLookup | null;
  loading: boolean;
  onReload: () => void;
  onCustomerUpdated: () => void;
  /** Migration 245: „Anderen Namen versuchen" öffnet das Kunden-Formular auf dem Firmenfeld. */
  onFirmaBearbeiten: () => void;
}) {
  // R2.6 (30.08.2026): Strasse/PLZ mit rein - nur die Stadt konnte bei mehrdeutigen Firmennamen
  // (mehrere Filialen in derselben Stadt) zu ungenauen Treffern fuehren, obwohl die volle Adresse
  // im System stand. Server-Default (defaultPlacesQuery, routes/customers.ts) synchron gehalten.
  const ortsteil = [addressZip, addressCity].filter(Boolean).join(" ");
  const defaultQuery = [company, addressStreet, ortsteil || null].filter(Boolean).join(", ");
  const [query, setQuery] = useState(lookup?.query ?? defaultQuery);
  const [busy, setBusy] = useState(false);
  const [applyingField, setApplyingField] = useState<GooglePlacesApplicableField | null>(null);
  const [waehlend, setWaehlend] = useState<string | null>(null);
  /** Migration 245: läuft gerade ein „keiner davon" / „verwerfen"? */
  const [verwerfend, setVerwerfend] = useState(false);
  /** Der Satz zur Ablehnung — bewusst NICHT als roter Fehler (`error`): eine abgewiesene
   *  Entscheidung ist eine Auskunft, kein Defekt (Lehre aus R0.7). */
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * R3.3 (§ 4.7): Steht eine Trefferauswahl offen? Die ganze Entscheidung („wann überhaupt",
   * „welche Zeilen", „welcher Satz") liegt in der geprüften Regel — hier wird nur gerendert.
   */
  const auswahl = trefferauswahl({
    status: lookup?.status ?? null,
    treffer: lookup?.candidates ?? null,
    lookedUpAt: lookup?.lookedUpAt ?? null,
  });

  /**
   * R4.5: GENAU die Werte, die diese Karte aus dem Google-Abruf anzeigt — die Grundlage sowohl
   * fuer die Attribution als auch fuer den Verfallshinweis. Beide Fragen („steht hier etwas von
   * Google?") sind dieselbe; sie zweimal zu beantworten war der Weg, auf dem sie auseinanderlaufen.
   * `openingHours` wird auf seine Zeilen heruntergebrochen, weil ein Objekt mit leerer Liste
   * nichts anzeigt und deshalb auch nichts zu attribuieren gibt.
   * Bei einer offenen Trefferauswahl (R3.3) stehen die Google-Angaben in den Treffern, nicht in
   * den (dann leeren) Vorschlagsfeldern — die Regel liefert sie deshalb selbst mit.
   */
  const googleWerte: readonly unknown[] = lookup
    ? [
        lookup.suggestedName,
        lookup.suggestedFormattedAddress,
        lookup.suggestedPhone,
        lookup.rating,
        lookup.userRatingsTotal,
        lookup.businessStatus,
        lookup.openingHours?.weekdayDescriptions ?? null,
        ...(auswahl?.googleWerte ?? []),
      ]
    : [];

  // Suchfeld beim Kundenwechsel neu vorbefuellen (bestehende query, falls schon abgefragt, sonst
  // der Default aus Firma+Ort).
  useEffect(() => {
    setQuery(lookup?.query ?? defaultQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  async function handleLookup() {
    setBusy(true);
    setError(null);
    try {
      await apiMutate(`/api/customers/${customerId}/places-lookup`, "POST", query.trim() ? { query: query.trim() } : {});
      onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleApply(field: GooglePlacesApplicableField) {
    if (!lookup?.id || !lookup.lookedUpAt) return;
    setApplyingField(field);
    setError(null);
    try {
      // M-45: (id, lookedUpAt) = Version des angezeigten Ergebnisses - der Server lehnt mit 409
      // ab, wenn ein anderer Tab den Lookup inzwischen ausgetauscht hat (kein stilles Uebernehmen).
      await apiMutate(`/api/customers/${customerId}/places-lookup`, "PATCH", { appliedField: field, lookupId: lookup.id, lookedUpAt: lookup.lookedUpAt });
      onReload();
      onCustomerUpdated();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(/lookup_changed|neu abgerufen/i.test(msg)
        ? "Das Ergebnis wurde inzwischen neu abgerufen – bitte den aktuellen Vorschlag prüfen und erneut übernehmen."
        : msg);
      onReload(); // aktuellen Vorschlag anzeigen
    } finally {
      setApplyingField(null);
    }
  }

  /**
   * R3.3: Der Mensch entscheidet, welcher Betrieb gemeint ist. Der Aufruf kostet nichts (die
   * Treffer stehen bereits aus DEMSELBEN Abruf in der Zeile) und schreibt NICHTS in den Kunden —
   * er macht den gewählten Treffer zum Vorschlag. Übernommen wird danach wie immer Feld für Feld.
   */
  async function handleAuswahl(placeId: string) {
    if (!lookup?.id || !lookup.lookedUpAt) return;
    setWaehlend(placeId);
    setError(null);
    try {
      await apiMutate(`/api/customers/${customerId}/places-lookup/auswahl`, "PATCH", {
        placeId,
        lookupId: lookup.id,
        lookedUpAt: lookup.lookedUpAt,
      });
      onReload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(/lookup_changed|neu abgerufen/i.test(msg)
        ? "Das Ergebnis wurde inzwischen neu abgerufen – bitte die aktuelle Trefferliste prüfen und erneut auswählen."
        : msg);
      onReload();
    } finally {
      setWaehlend(null);
    }
  }

  /**
   * Migration 245 — „keiner davon" (bei mehreren Treffern) und „Verwerfen" (bei einem Vorschlag).
   *
   * EIN Weg für beide, weil es dieselbe Tatsache ist: das Google-Ergebnis beschreibt diesen
   * Kunden nicht. Die Zeile bleibt stehen — mit `status='rejected'` und dem Zeitpunkt der
   * Entscheidung, ohne die Google-Inhalte. Ein Löschen wäre von „nie nachgeschlagen" nicht zu
   * unterscheiden, und die Karte fragte beim nächsten Öffnen wieder danach.
   */
  async function verwerfen() {
    if (!lookup?.id || !lookup.lookedUpAt) return;
    setVerwerfend(true);
    setHinweis(null);
    setError(null);
    const { hinweis: h } = await placesErgebnisVerwerfen(customerId, {
      lookupId: lookup.id,
      lookedUpAt: lookup.lookedUpAt,
    });
    setHinweis(h);
    setVerwerfend(false);
    onReload();
  }

  /** Migration 245: der Zustandssatz zur Ablehnung kommt aus derselben reinen Regel wie im
   *  Handelsregister — ein Wortlaut für beide Quellen, kein zweiter Satz für dieselbe Tatsache. */
  const verworfenAnzeige =
    lookup?.status === "rejected"
      ? anreicherungsAnzeige({
          modulAktiv: true,
          modulName: MODUL_PLACES,
          firma: company ?? "",
          jetzt: new Date(),
          zeile: {
            status: lookup.status,
            lookedUpAt: lookup.lookedUpAt,
            createdAt: lookup.createdAt,
            verworfenAm: lookup.verworfenAm,
          },
        })
      : null;

  return (
    <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low p-5 dark:border-outline/30 dark:bg-surface-variant/10">
      <p className="mb-3 font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
        Google Places
      </p>

      <div className="flex items-center gap-2">
        <Input
          className="flex-1"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Firma, Ort …"
        />
        <Button
          type="button"
          variant="secondary"
          className="shrink-0 !py-2"
          disabled={busy || !query.trim()}
          onClick={() => void handleLookup()}
        >
          {busy ? "Sucht …" : "Nachschlagen"}
        </Button>
      </div>
      {error && <ErrorNote message={error} />}
      {/* Migration 245: eine abgewiesene Entscheidung ist eine Auskunft, kein Defekt — deshalb
          als Satz und nicht als rote Fehlerbox. */}
      {hinweis && <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant/70">{hinweis}</p>}

      {loading ? (
        <div className="mt-3">
          <LoadingRows rows={2} />
        </div>
      ) : verworfenAnzeige ? (
        /* Migration 245: „als unpassend vermerkt". Der Satz kommt aus `@maestro/anreicherung` —
           derselbe, der über einem verworfenen Registerergebnis steht. Die Google-Inhalte sind
           weg (die Route räumt sie sofort statt erst mit der Frist aus R2.7), die Suchanfrage
           steht oben und erklärt, wonach gesucht wurde. */
        <div className="mt-3 space-y-2">
          <p className="font-body-sm text-body-sm text-on-surface-variant/70">{verworfenAnzeige.satz}</p>
          <div className="flex flex-wrap items-baseline gap-x-3">
            {verworfenAnzeige.handlung?.art === "firma_bearbeiten" && (
              <button
                type="button"
                onClick={onFirmaBearbeiten}
                className="font-label-md text-label-md text-primary hover:underline dark:text-primary-fixed-dim"
              >
                {verworfenAnzeige.handlung.beschriftung}
              </button>
            )}
            {/* Anders als im Handelsregister ist der Weg weiter hier ein neuer, bezahlter Abruf
                mit genauerer Suchanfrage — der Knopf dafür steht oben und bleibt bedienbar. */}
            <span className="font-body-sm text-body-sm text-on-surface-variant/70">
              Oder die Suchanfrage oben genauer fassen und erneut „Nachschlagen" klicken.
            </span>
          </div>
        </div>
      ) : !lookup ? (
        <p className="mt-3 font-body-sm text-body-sm text-on-surface-variant/70">
          {/* Derselbe Zustandsname wie im Handelsregister (§ 4.2 „nie gefragt") — hier MIT
              Handlung, weil es für Places einen echten manuellen Abruf gibt (Knopf oben). */}
          Noch nicht abgerufen — Suchanfrage oben prüfen und „Nachschlagen" klicken.
        </p>
      ) : auswahl ? (
        /* R3.3 (§ 4.7, Befund B6): Google hat mehrere Betriebe geliefert. Bis hierher nahm der
           Code stumm den ersten — bei drei gleichnamigen Betrieben also die Anschrift eines
           fremden Ladens. Jetzt steht die Frage auf dem Schirm, und der Kundendatensatz bleibt
           unverändert, bis jemand geantwortet hat: die Vorschlagsfelder sind bei Mehrdeutigkeit
           leer, es gibt also gar nichts zu übernehmen (der Server weist es zusätzlich ab). */
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <p className="font-body-sm text-body-sm text-on-surface-variant">{auswahl.satz}</p>
          </div>
          <div className="flex flex-wrap items-baseline gap-x-2">
            <Herkunft zeile={auswahl.herkunft} />
            <GoogleAttribution werte={googleWerte} />
          </div>
          <ul className="space-y-2">
            {auswahl.eintraege.map((eintrag) => (
              <li
                key={eintrag.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-outline-variant/30 p-3 dark:border-outline/20"
              >
                <div className="min-w-0">
                  <p className="truncate font-label-md text-label-md text-on-background dark:text-inverse-on-surface">
                    {eintrag.titel}
                  </p>
                  {eintrag.zeilen.map((zeile) => (
                    <p key={zeile} className="truncate font-body-sm text-body-sm text-on-surface-variant/80">
                      {zeile}
                    </p>
                  ))}
                  {/* R4.2 schon hier: „dauerhaft geschlossen" ist genau die Angabe, an der sich
                      zwei gleichnamige Betriebe unterscheiden lassen. */}
                  {eintrag.warnung && (
                    <p className="mt-0.5 font-body-sm text-body-sm text-secondary dark:text-[#b8ae9e]">
                      {eintrag.warnung}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="shrink-0 !px-3 !py-1.5"
                  disabled={waehlend !== null || verwerfend}
                  onClick={() => void handleAuswahl(eintrag.id)}
                >
                  {waehlend === eintrag.id ? "…" : "Das ist er"}
                </Button>
              </li>
            ))}
          </ul>
          {/* Migration 245: Bis hierher stand hier NUR ein Hinweis, was der Betrieb tun könnte —
              die Ablehnung selbst konnte er nirgends festhalten. Jetzt schon: „keiner passt" ist
              eine Tatsache und wird als solche gespeichert (`status='rejected'`), statt dass der
              Vorschlag still liegen bleibt und beim nächsten Öffnen dieselbe Frage stellt. */}
          <div className="flex flex-wrap items-baseline gap-x-3">
            <button
              type="button"
              disabled={waehlend !== null || verwerfend}
              onClick={() => void verwerfen()}
              className="font-label-md text-label-md text-primary hover:underline disabled:opacity-50 dark:text-primary-fixed-dim"
            >
              {verwerfend ? "Wird vermerkt …" : "Keiner davon"}
            </button>
            <span className="font-body-sm text-body-sm text-on-surface-variant/70">
              Oder die Suchanfrage oben genauer fassen und erneut „Nachschlagen" klicken.
            </span>
          </div>
        </div>
      ) : !lookup.placeId ? (
        <div className="mt-3">
          <p className="font-body-sm text-body-sm text-on-surface-variant/70">
            Keine Treffer für „{lookup.query}".
          </p>
          {/* Auch „kein Treffer" ist ein Ergebnis mit einem Alter. */}
          <Herkunft zeile={standZeile("Google Places", lookup.lookedUpAt)} />
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {/* Bewertung, Status, Öffnungszeiten und die drei Vorschlagsfelder stammen aus EINEM
              Abruf — eine Herkunftszeile fuer den ganzen Block, ganz oben, damit sie ueber jedem
              Wert steht statt darunter zu verschwinden. `lookedUpAt` ist hier fast immer gesetzt
              (die Route schreibt es), aber ein Rueckfall auf `createdAt` findet trotzdem nicht
              statt: fehlt es, sagt die Zeile „ohne Datum". */}
          <div className="flex flex-wrap items-baseline gap-x-2">
            <Herkunft zeile={standZeile("Google Places", lookup.lookedUpAt)} />
            {/* R4.5 / § 6.2: die Attributionspflicht. Sie steht hier neben dem Stand, nicht
                statt seiner — der Stand sagt, wie alt die Angabe ist, die Attribution, wem sie
                gehört. Ob sie erscheint, entscheidet `googleAttribution()` an denselben Werten,
                die unten gerendert werden (`googleWerte`). */}
            <GoogleAttribution werte={googleWerte} />
          </div>
          {/* R2.7 / P-6: nach Fristablauf hat der Sweep die Google-Inhalte aus der Zeile
              entfernt (Maps-Platform-Terms § 3.2.3, Konzept § 6.2) — die `place_id` bleibt.
              Ohne diesen Satz stünde hier eine leere Karte mit einer Herkunftszeile, und der
              Betreiber müsste raten, ob der Abruf nichts fand oder die Daten verfallen sind.
              Dieselbe Prüfung („steht überhaupt eine Google-Angabe auf dem Schirm?") trägt seit
              R4.5 die Attribution — deshalb kommt sie aus derselben Regel und nicht mehr aus
              einer von Hand gepflegten Kette von sechs Feldern, die beim siebten still
              danebenläge. */}
          {!zeigtGoogleAngaben(googleWerte) && (
            <p className="font-body-sm text-body-sm text-on-surface-variant/70">
              Die Google-Angaben zu diesem Eintrag sind verfallen und wurden entfernt — bereits
              übernommene Werte im Kundendatensatz bleiben davon unberührt. „Nachschlagen"
              holt einen aktuellen Stand.
            </p>
          )}
          {/* Immer sichtbarer Lese-Block — rein informativ, nie übernehmbar (kein customers-
              Spalten-Gegenstück für Bewertung/Öffnungszeiten/Status). */}
          {(lookup.rating != null || lookup.businessStatus) && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 font-body-sm text-body-sm">
              {lookup.rating != null && (
                <>
                  <dt className="text-secondary dark:text-secondary-fixed-dim">Bewertung</dt>
                  <dd className="text-on-background dark:text-inverse-on-surface">
                    {lookup.rating.toFixed(1)} ★{lookup.userRatingsTotal != null ? ` (${lookup.userRatingsTotal})` : ""}
                  </dd>
                </>
              )}
              {lookup.businessStatus && (
                <>
                  <dt className="text-secondary dark:text-secondary-fixed-dim">Status</dt>
                  <dd className="text-on-background dark:text-inverse-on-surface">{lookup.businessStatus}</dd>
                </>
              )}
            </dl>
          )}
          {lookup.openingHours && lookup.openingHours.weekdayDescriptions.length > 0 && (
            <ul className="space-y-0.5 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
              {lookup.openingHours.weekdayDescriptions.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          )}

          {/* Je Feld: "Übernehmen"-Button ODER "✓ übernommen" — appliedFields ist reiner
              UI-Zustand, kein Verbot, ein Feld erneut zu übernehmen. */}
          <div className="space-y-2">
            {PLACES_FIELDS.map(({ key, label, value }) => {
              const suggested = value(lookup);
              if (!suggested) return null;
              const applied = lookup.appliedFields[key] === true;
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3 rounded-lg border border-outline-variant/30 p-3 dark:border-outline/20"
                >
                  <div className="min-w-0">
                    <p className="font-label-md text-[11px] uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                      {label}
                    </p>
                    <p className="truncate font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                      {suggested}
                    </p>
                  </div>
                  {applied ? (
                    <span className="flex shrink-0 items-center gap-1 font-label-md text-label-md text-secondary dark:text-[#b8ae9e]">
                      <Icon name="check_circle" className="text-[16px]" /> übernommen
                    </span>
                  ) : (
                    <Button
                      type="button"
                      variant="secondary"
                      className="shrink-0 !px-3 !py-1.5"
                      disabled={applyingField === key}
                      onClick={() => void handleApply(key)}
                    >
                      {applyingField === key ? "…" : "Übernehmen"}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Migration 245: das Gegenstück zu „Übernehmen". Bis hierher war die Frist aus R2.7
              das EINZIGE Ende, das ein Vorschlag fand — wer sah, dass Google den falschen Betrieb
              geliefert hat, konnte ihn nur stehen lassen. Der Vermerk räumt die Google-Angaben
              sofort weg; bereits übernommene Werte im Kundendatensatz bleiben unberührt (sie sind
              seit der Übernahme eigene Stammdaten, und ein Verwerfen ist kein Widerruf). */}
          <div>
            <button
              type="button"
              disabled={verwerfend || applyingField !== null}
              onClick={() => void verwerfen()}
              className="font-label-md text-label-md text-primary hover:underline disabled:opacity-50 dark:text-primary-fixed-dim"
            >
              {verwerfend ? "Wird vermerkt …" : "Gehört nicht zu diesem Kunden"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Punkt 41 (Antoine, 26.09.2026): die beim Kunden gemerkte Kommunikationssprache, direkt auf der
 * Kontaktkarte umstellbar. Sie ist der Startwert jedes NEUEN Vorgangs dieses Kunden (Vorrang vor
 * der Anfragesprache); bestehende Vorgänge bleiben unverändert.
 */
const KEINE_SPRACHE = "__keine__";
function KundenSprache({ kundeId, sprache, onGeaendert }: { kundeId: string; sprache: Kommunikationssprache | null; onGeaendert: () => void }) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  async function setzen(wert: string) {
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/customers/${kundeId}`, "PATCH", { sprache: wert === KEINE_SPRACHE ? null : wert });
      onGeaendert();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div data-testid="kunden-sprache" className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2.5 text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
        <Icon name="translate" className="text-[16px] text-secondary" /> Kommunikationssprache
        <div className="w-44">
          <Select
            ariaLabel="Kommunikationssprache des Kunden"
            value={sprache ?? KEINE_SPRACHE}
            disabled={busy}
            onValueChange={(v) => void setzen(v)}
            options={[
              { value: KEINE_SPRACHE, label: "nicht festgelegt" },
              ...KOMMUNIKATIONSSPRACHEN.map((s) => ({ value: s, label: SPRACH_LABEL[s] })),
            ]}
          />
        </div>
      </div>
      <span className="text-[11px] text-secondary dark:text-secondary-fixed-dim">Neue Vorgänge dieses Kunden starten in dieser Sprache.</span>
      {fehler && <span className="text-[12px] text-error">{fehler}</span>}
    </div>
  );
}

/**
 * Punkt 42 (Migration 533, Mockup freigegeben 26.09.2026): der Kundentyp am Kunden. Ist er nur
 * automatisch erkannt, steht das sichtbar daneben — ein Klick auf irgendeinen Wert (auch den
 * erkannten) bestätigt ihn. Gilt für alle Aufträge dieses Kunden; der Vertrag richtet sich danach.
 */
function KundentypKarte({ kunde, onGeaendert }: { kunde: Customer; onGeaendert: () => void }) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const typ = kundentypVon(kunde);
  const automatisch = kunde.kundentypAutomatisch ?? false;
  async function waehlen(t: Kundentyp) {
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/customers/${kunde.id}`, "PATCH", { kundentyp: t });
      onGeaendert();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card data-testid="kundentyp-karte">
      <CardRow title="Kundentyp" />
      <div className="flex flex-col gap-2.5 px-[18px] py-[14px]">
        {automatisch && (
          <div>
            <AutomatischErkanntChip />
          </div>
        )}
        <KundentypAuswahl wert={typ} onWahl={(t) => void waehlen(t)} disabled={busy} />
        <span className="text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
          {automatisch
            ? erkennungsGrund(typ)
            : typ === "privat"
              ? "Die Kostenübernahme läuft auf den Personennamen, mit der Vorlage für Privatpersonen (falls hinterlegt). Gilt für alle Aufträge dieses Kunden."
              : `Die Kostenübernahme läuft auf ${typ === "verein" ? "den Verein" : "die Firma"}, mit der Vorlage für Unternehmen. Gilt für alle Aufträge dieses Kunden.`}
        </span>
        {fehler && <span className="text-[12px] text-error">{fehler}</span>}
      </div>
    </Card>
  );
}
